import React, { useState, useEffect, useMemo, useRef } from "react";
import { Allotment } from "allotment";
import "allotment/dist/style.css";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { typeToIconMapping } from "../utils";
import DeleteIcon from "@mui/icons-material/Delete";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import {
  Box,
  Dialog,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Typography,
  styled,
  Autocomplete,
  Avatar,
  useTheme,
  CircularProgress,
  SelectChangeEvent,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Button,
  Chip,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import AnnotationEditor, { AnnotationEditorRef } from "./AnnotationEditor";
import AttachmentViewModal from "./AttachmentViewModal";
import AttachmentPreview from "./AttachmentPreview";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import {
  useCreateTagMutation,
  useCreateBugMutation,
  useGetConfigDetailsQuery,
  useGetListOfBugsQuery,
  useGetListOfProjectsQuery,
  useGetListOfStatusQuery,
  useGetListOfTagsQuery,
  useGetProjectByIdQuery,
  useGetProjectStatusLabelsMutation,
  useGetSprintListQuery,
  useGetListOfMembersQuery,
} from "../../../features/bughurd/api";
import { useNavigate, useParams } from "react-router-dom";
import storageService from "@/Components/helpers/storageService";
import { useGetAiCompanyConfigQuery } from "@/features/AiApi/chatApi";
import AiNotConfiguredDialog from "@/Components/Common/AiNotConfiguredDialog";
import { LoadingButton } from "@mui/lab";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import HighestIcon from "@/assets/icons/highest.svg?react";
import HighIcon from "@/assets/icons/high.svg?react";
import MediumIcon from "@/assets/icons/medium.svg?react";
import LowIcon from "@/assets/icons/low.svg?react";
import { hexToRgba } from "@/Components/helpers/util";
import {
  StyledChip,
  StyledSelect,
  StyledTextField,
} from "@/pages/Board/bugherdComponents/styled";
import CustomAutocomplete from "./CustomAutocomplete";
import RichTextEditor from "@/Components/Common/RichTextEditor";
import {
  getOperatingSystemInfo,
  getActiveSprints,
  getDraftSprints,
} from "@/pages/Board/utils";
import { useAppSelector } from "@/app/hooks";
import { selectBoardState } from "@/features/bughurd/boardSelector";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import VideoPreviewPanel from "./VideoPreviewPanel";
import VoiceRecorder from "@/Components/VoiceRecorder";
import {
  DescriptionVoiceRecorder,
  SummaryVoiceRecorder,
} from "@/Components/VoiceRecorder";
import {
  processVideoTranscription,
  isVideoFile,
} from "@/utils/videoTranscription";

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    width: "95vw",
    height: "90vh",
    maxWidth: "80vw",
    maxHeight: "85vh",
    margin: 0,
    padding: 0,
    borderRadius: 12,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 12px 48px rgba(0, 0, 0, 0.4)"
        : "0 12px 48px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
  },
  "& .split-view-view": {
    overflow: "hidden",
  },
  "& .sash-container .sash": {
    backgroundColor: "transparent !important",
    width: "4px !important",
    cursor: "col-resize",
    "&:hover, &.active": {
      backgroundColor: `${theme.palette.primary.main}40 !important`,
    },
    "&::before": {
      display: "none !important",
    },
  },
}));

const DropZone = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[300]}`,
  borderRadius: "12px",
  padding: theme.spacing(4, 2),
  minHeight: "160px",
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.5),
  textAlign: "center",
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.grey[50],
  cursor: "pointer",
  transition: "all 0.25s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.primary.main + "12"
        : theme.palette.primary.main + "08",
    transform: "translateY(-1px)",
    "& .dropzone-icon": {
      color: theme.palette.primary.main,
      transform: "scale(1.05)",
    },
  },
  "&.drag-over": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + "14",
    borderStyle: "solid",
    transform: "scale(1.01)",
  },
}));

interface TaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  coordinates?: { left: number; top: number } | null;
  selectedElement?: HTMLElement | null;
  pageUrl: string;
  screenshotData?: string | null;
  users: any[];
  onSubmit: (
    formData: TaskData,
    attachments: AttachmentType[],
    availableUsers: any[],
  ) => Promise<void>;
  isSubmitting: boolean;
  selector?: string;
  fullPageScreenshotData?: string | null;
  imageUpload: boolean;
  blobDataForFullPage?: Blob | null;
  recordedURL?: string;
  recordBugLoading?: boolean;
  videoRecording?: Blob | null | any;
  status?: string | any;
  refreshColumnData?: any;
  setDetailView?: React.Dispatch<React.SetStateAction<boolean>>;
  permissions?: any;
  consoleLogs?: any[];
  networkLogs?: any[];
}

export interface AttachmentType {
  fileName: string;
  fileType: string;
  url: string;
  type?: string; // "video", "image", etc.
  serverUrl?: string;
  isUploading?: boolean;
  uploadFailed?: boolean;
  isAnnotated?: boolean;
}

interface TaskData {
  summary: string;
  description: string;
  platform: string;
  severity: string;
  status: string;
  tags: string[];
  assignees: string[];
  keepSettings: boolean;
  screenshot: string | null;
  fullPageScreenshot: string | null;
  project: string;
  projectKey: string;
  bugStatus?: string;
  jiraPriority?: string;
  jiraStatus?: string;
  pageUrl?: string;
  isSettings?: boolean;
  selector?: string;
  screenShotKey?: string;
  fullPageScreenshotKey?: string;
  recordingLink?: string;
  type?: string;
  videoRecording?: Blob | null | any;
  jiraProjectKey?: string;
  jiraProjectName?: string;
  jiraProjectId?: string;
  startDate?: string;
  dueDate?: string;
  estimation?: string;
  estimationDays?: string;
  estimationHours?: string;
  estimationMinutes?: string;
  boardType?: string;
  currentSprint?: string | null;
  isBackLog?: boolean;
}

export const defaultTaskData: TaskData = {
  summary: "",
  description: "",
  platform: "",
  severity: "Medium",
  status: "",
  tags: [],
  assignees: [],
  keepSettings: false,
  screenshot: null,
  fullPageScreenshot: null,
  project: "",
  projectKey: "",
  screenShotKey: "",
  fullPageScreenshotKey: "",
  recordingLink: "",
  type: "DEFECT",
  videoRecording: null,
  jiraProjectKey: "",
  jiraProjectName: "",
  jiraProjectId: "",
  startDate: null,
  dueDate: null,
  estimation: "",
  estimationDays: "",
  estimationHours: "",
  estimationMinutes: "",
  boardType: "",
  currentSprint: null,
  isBackLog: false,
};

const TaskCreateModal: React.FC<TaskCreateModalProps> = ({
  open,
  onClose,
  pageUrl,
  users,
  screenshotData,
  onSubmit,
  setDetailView,
  isSubmitting,
  selector,
  fullPageScreenshotData,
  imageUpload,
  blobDataForFullPage,
  recordedURL,
  recordBugLoading,
  videoRecording,
  status,
  refreshColumnData,
  permissions,
  consoleLogs,
  networkLogs,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: aiConfigData } = useGetAiCompanyConfigQuery({});
  const isAiConfigured = useMemo(() => {
    const providers = aiConfigData?.data?.apiProviders || [];
    return providers?.some((p: any) => p?.isActive && p?.apiKey?.trim());
  }, [aiConfigData]);

  const [showAiConfigDialog, setShowAiConfigDialog] = useState(false);

  const baseUrl = window?.location?.origin;
  const isAta =
    baseUrl?.includes("localhost") || baseUrl?.includes("ata") || false;

  const annotationEditorRef = useRef<AnnotationEditorRef>(null);
  const inlineAnnotationEditorRef = useRef<AnnotationEditorRef>(null);
  const [inlineAttachmentIndex, setInlineAttachmentIndex] = useState<
    number | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [taskData, setTaskData] = useState<TaskData>(defaultTaskData);
  const [tagInput, setTagInput] = useState("");
  const [isAnnotationEditorOpen, setIsAnnotationEditorOpen] = useState(false);
  const [includeFullPageScreenshot, setIncludeFullPageScreenshot] =
    useState(true);
  const [attachments, setAttachments] = useState<AttachmentType[]>([]);
  const [_statusList, setStatusList] = useState([]);
  const [isJiraConfigured, setIsJiraConfigured] = useState(false);
  const [selectedAttachment, setSelectedAttachment] =
    useState<AttachmentType | null>(null);
  const [isAttachmentViewOpen, setIsAttachmentViewOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [_isUploading, setIsUploading] = useState(false);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [pendingUploads, setPendingUploads] = useState<Set<string>>(new Set());
  const boardState = useAppSelector(selectBoardState);
  const { roleCodeDetails: myPermissions } = useAppSelector(
    (state) => state.emailAuth,
  );
  const [selectedEpic, setSelectedEpic] = useState<string | null>(null);

  // Store original filenames for display purposes (not sent to backend)
  const [originalFilenames, setOriginalFilenames] = useState<
    Map<string, string>
  >(new Map());

  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeTab, _setActiveTab] = useState("1");

  // Video preview state
  const [isVideoPreviewOpen, setIsVideoPreviewOpen] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);

  // Validation error states
  const [statusError, setStatusError] = useState("");
  const [severityError, setSeverityError] = useState("");
  const [issueTypeError, setIssueTypeError] = useState("");
  const [projectError, setProjectError] = useState("");
  const [sprintError, setSprintError] = useState("");
  const [storyError, setStoryError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);

  // Story Link Feature State
  const [selectedStory, setSelectedStory] = useState<string | null>(null);
  const [storyCreationData, setStoryCreationData] = useState({
    summary: "",
    description: "",
  });
  const [isCreatingStory, setIsCreatingStory] = useState(false);
  const [storyTab, setStoryTab] = useState<"create" | null>(null);

  // Voice recording state for summary
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [isVoiceFinalizing, setIsVoiceFinalizing] = useState(false);
  const [summaryProcessingMessage, setSummaryProcessingMessage] = useState("");

  // Voice recording state for description
  const [isDescVoiceRecording, setIsDescVoiceRecording] = useState(false);

  // Video transcription state
  const [transcribingVideos, setTranscribingVideos] = useState<Set<string>>(
    new Set(),
  );
  const [transcriptionProgress, setTranscriptionProgress] = useState<
    Map<string, string>
  >(new Map());
  const [videoFiles, setVideoFiles] = useState<Map<string, File>>(new Map());

  // Modal maximize state
  const [isMaximized, setIsMaximized] = useState(true);

  // Get the current project ID (either from URL param or taskData)
  const currentProjectId = taskData.project;

  const { data: tagList, refetch: refetchTags } = useGetListOfTagsQuery(
    currentProjectId,
    {
      skip: !currentProjectId,
    },
  );
  const availableTags = tagList?.data?.decryptedData?.items || [];

  const [createTag] = useCreateTagMutation();

  const { data: statusLists } = useGetListOfStatusQuery(
    { projectId: currentProjectId },
    {
      skip: !currentProjectId,
    },
  );

  // Fetch project members from API
  const { data: projectMembersData } = useGetListOfMembersQuery(
    { projectId: currentProjectId, page: 1, size: 100, searchName: "" },
    {
      skip: !currentProjectId,
    },
  );
  const { data: epicData } = useGetListOfBugsQuery(
    {
      id: currentProjectId,
      forBoard: true,
      viewType: "TIMELINE",
      showEpic: true,
      page: 1,
      limit: 100,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const epics = useMemo(() => {
    if (!epicData?.data?.decryptedData?.items) return [];
    return epicData?.data?.decryptedData?.items?.map((item: any) => ({
      id: item?._id,
      name: item?.summary,
    }));
  }, [epicData]);

  // Fetch stories for linking to defects
  const {
    data: storyData,
    refetch: refetchStories,
    isFetching: isStoriesFetching,
  } = useGetListOfBugsQuery(
    {
      id: currentProjectId,
      type: "STORY",
      page: 1,
      limit: 100,
    },
    {
      skip:
        !currentProjectId ||
        (taskData?.type !== "BUG" &&
          taskData?.type !== "DEFECT" &&
          taskData?.type !== "SUGGESTIONS"),
      refetchOnMountOrArgChange: true,
    },
  );
  const stories = useMemo(() => {
    if (!storyData?.data?.decryptedData?.items) return [];
    return storyData?.data?.decryptedData?.items?.map((item: any) => ({
      id: item?._id,
      summary: item?.summary,
      status: item?.status,
      severity: item?.severity,
    }));
  }, [storyData]);

  // Create story mutation
  const [createBug] = useCreateBugMutation();

  const { data: sprintListData, isLoading: isSprintLoading } =
    useGetSprintListQuery(
      {
        projectId: taskData.project || "",
        page: 1,
        limit: 100,
        statuses: ["DRAFT", "ACTIVE"],
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !taskData.project,
      },
    );
  const sprints = sprintListData?.data?.decryptedData?.items || [];
  const draftSprints = getDraftSprints(sprints);
  const activeSprints = getActiveSprints(sprints);

  const availableStatus = statusLists?.data?.decryptedData?.items || [];

  // Set default status to first available status when statuses load and no status is selected
  useEffect(() => {
    if (availableStatus?.length > 0 && !taskData.status) {
      const defaultStatus = availableStatus[0]?._id || "";
      setTaskData((prev) => ({ ...prev, status: defaultStatus }));
    }
  }, [availableStatus, taskData.status]);

  const [getProjectStatusLabels, { isLoading: isLoadingStatusLabels }] =
    useGetProjectStatusLabelsMutation();
  const { data: configDataList } = useGetConfigDetailsQuery({});
  const configData = configDataList?.data?.decryptedData;

  // const { projects: projectList } = useAppSelector((state) => state.board);
  const {
    data: projects,
    // isLoading: projectsLoading,
    // isFetching: projectsFetching,
  } = useGetListOfProjectsQuery({ searchName: "", page: 1, size: 100 });
  const projectList = projects?.data?.decryptedData?.items || [];
  const { data: projectDetails, isLoading: isProjectLoading } =
    useGetProjectByIdQuery(currentProjectId, {
      skip: !currentProjectId,
      refetchOnMountOrArgChange: true,
    });

  useEffect(() => {
    if (
      projectDetails?.data?.decryptedData?.boardType &&
      taskData?.project &&
      !taskData?.boardType
    ) {
      const currentBoardType = projectDetails?.data?.decryptedData.boardType;

      setTaskData((prev) => ({
        ...prev,
        boardType: currentBoardType,
      }));
    }
  }, [projectDetails, taskData.project]);

  const jiraProjectList = projectDetails?.data?.decryptedData.jiraConfig;
  const projectUserIds = (projectDetails?.data?.decryptedData.users || []).map(
    (user) => {
      if (typeof user === "object" && user?._id) {
        return user._id;
      }
      // Fallback: if it's already a string ID
      return user;
    },
  );

  const availableUsers = useMemo(() => {
    if (!taskData?.project) {
      return [];
    }
    // Use project members from API
    return (
      projectMembersData?.data?.decryptedData?.items?.filter(
        (user) =>
          user?.userRole === "TEAM_MEMBER" || user?.userRole === "TEAM_LEAD",
      ) || []
    );
  }, [taskData?.project, projectMembersData]);

  // Check if Sprint dropdown should be shown
  const shouldShowSprintDropdown = useMemo(() => {
    return taskData.boardType === "SCRUM" && activeSprints.length > 0;
  }, [taskData.boardType, activeSprints]);

  // Check if media content exists (screenshot, video, loading, or inline attachment)
  const hasMediaContent = useMemo(() => {
    return !!(
      inlineAttachmentIndex !== null ||
      taskData?.videoRecording ||
      taskData?.screenshot ||
      taskData?.fullPageScreenshot ||
      recordBugLoading
    );
  }, [
    inlineAttachmentIndex,
    taskData?.videoRecording,
    taskData?.screenshot,
    taskData?.fullPageScreenshot,
    recordBugLoading,
  ]);

  // Check if Story Link section should be shown
  const shouldShowStoryLink = useMemo(() => {
    const isDefectType =
      taskData?.type === "BUG" ||
      taskData?.type === "DEFECT" ||
      taskData?.type === "SUGGESTIONS";

    return isDefectType;
  }, [taskData?.type]);

  // const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
  //   setActiveTab(newValue);
  // };

  const handleChange = (fieldKey: string, value: string, type?: string) => {
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: type === "number" ? Number(value) : value,
    }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const getDefaultFields = (jiraProjectKey) => {
    const jiraConfig = projectDetails?.data?.decryptedData?.jiraConfig;

    // Check if jiraConfig exists and is an array
    if (!jiraConfig || !Array.isArray(jiraConfig)) {
      return null;
    }

    const selectedProject = jiraConfig.find(
      (project) => project.key === jiraProjectKey,
    );

    return selectedProject ? selectedProject.defaultFields : null;
  };

  const defaultFields = getDefaultFields(taskData?.jiraProjectKey);

  const severityOptions = [
    { value: "Highest", label: t("Highest"), icon: <HighestIcon /> },
    { value: "High", label: t("High"), icon: <HighIcon /> },
    { value: "Medium", label: t("Medium"), icon: <MediumIcon /> },
    { value: "Low", label: t("Low"), icon: <LowIcon /> },
  ];
  const getIssueTypeIcon = (type: string) => {
    const typeInfo = typeToIconMapping[type as keyof typeof typeToIconMapping];
    if (!typeInfo) return null;
    return (
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "3px",
          backgroundColor: typeInfo.bgcolor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {typeInfo.icon}
      </Box>
    );
  };

  const issueType = [
    { value: "STORY", label: t("Story"), icon: getIssueTypeIcon("STORY") },
    { value: "DEFECT", label: t("Defect"), icon: getIssueTypeIcon("DEFECT") },
    { value: "EPIC", label: t("Epic"), icon: getIssueTypeIcon("EPIC") },
    {
      value: "SUGGESTIONS",
      label: t("Suggestions"),
      icon: getIssueTypeIcon("SUGGESTIONS"),
    },
  ];
  const issueTypeItems = projectDetails?.data?.decryptedData?.itemTypes?.length
    ? projectDetails?.data?.decryptedData?.itemTypes
    : issueType;
  // // Generate days options (0-365)
  // const dayOptions = Array.from({ length: 366 }, (_, i) => ({
  //   value: i.toString(),
  //   label: i === 0 ? "0" : i.toString(),
  // }));

  // // Generate hours options (0-23)
  // const hourOptions = Array.from({ length: 24 }, (_, i) => ({
  //   value: i.toString(),
  //   label: i === 0 ? "0" : i.toString(),
  // }));

  // Generate minutes options (0, 15, 30, 45)
  const minuteOptions = [
    { value: "0", label: "0" },
    { value: "15", label: "15" },
    { value: "30", label: "30" },
    { value: "45", label: "45" },
  ];

  // Function to generate unique filename while preserving original for display
  const generateUniqueFilename = (originalFileName: string) => {
    const timestamp = new Date().getTime();
    const randomId = Math.random().toString(36).substring(2, 15);
    const lastDotIndex = originalFileName.lastIndexOf(".");

    if (lastDotIndex === -1) {
      // No extension
      return `${originalFileName}_${timestamp}_${randomId}`;
    }

    const nameWithoutExtension = originalFileName.substring(0, lastDotIndex);
    const extension = originalFileName.substring(lastDotIndex);

    return `${nameWithoutExtension}_${timestamp}_${randomId}${extension}`;
  };

  // Function to update estimation based on days, hours and minutes
  const updateEstimation = (days: string, hours: string, minutes: string) => {
    const daysNum = parseInt(days || "0");
    const hoursNum = parseInt(hours || "0");
    const minutesNum = parseInt(minutes || "0");

    const parts = [];
    if (daysNum > 0) {
      parts.push(`${daysNum}d`);
    }
    if (hoursNum > 0) {
      parts.push(`${hoursNum}h`);
    }
    if (minutesNum > 0) {
      parts.push(`${minutesNum}m`);
    }

    return parts.join(" ");
  };

  const handleClose = () => {
    setInlineAttachmentIndex(null);
    resetForm();
    onClose();
  };

  const base64ToBlob = (base64String) => {
    const byteCharacters = atob(base64String.split(",")[1]);
    const byteArrays = [];
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
    return new Blob([new Uint8Array(byteArrays)], { type: "image/png" });
  };

  const uploadScreenshot = async (
    base64Screenshot?: string,
    blobData?: Blob,
    originalName?: string,
  ) => {
    if (!base64Screenshot && !blobData) return null;

    const finalBlob = blobData || base64ToBlob(base64Screenshot!);

    // Generate unique filename for storage
    const uniqueFilename = originalName
      ? generateUniqueFilename(originalName)
      : `screenshot_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2)}.png`;

    const formData = new FormData();
    formData.append("file", finalBlob, uniqueFilename);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_COMPANY_API_URL}/file/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );

      return response?.data?.data?.localData?.Key || null;
    } catch (error) {
      console.error("Screenshot upload failed:", error);
      return null;
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    if (name === "summary" && value.length > 250) {
      toast.warning(t("Summary should be less than 250 characters"));
      return;
    }

    if (name === "description" && value.length > 2000) {
      toast.warning(t("Description should be less than 2000 characters"));
      return;
    }

    // For estimation days and hours inputs, only allow numbers
    if (
      (name === "estimationDays" || name === "estimationHours") &&
      value !== ""
    ) {
      const numValue = parseInt(value);
      if (isNaN(numValue) || numValue < 0) {
        return; // Don't update if invalid number
      }
    }

    setTaskData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleRichTextEditor = (e) => {
    setTaskData((prev) => ({
      ...prev,
      description: e,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<unknown>) => {
    const { name, value } = e.target;

    if (name === "project") {
      // Find the selected project to get its boardType
      const selectedProject = projectList?.find(
        (project) => project._id === value,
      );
      const boardType = selectedProject?.boardType || "";

      setTaskData((prev) => ({
        ...prev,
        project: value as string,
        boardType: boardType,
        status: "", // Clear status when project changes
        currentSprint: null, // Reset sprint selection when project changes
        isBackLog: false, // Reset backlog flag when project changes
      }));
    } else {
      setTaskData((prev) => ({
        ...prev,
        [name]: value as string,
        // Clear status when project changes
        ...(name === "project" && { status: "" }),
      }));
    }

    // Clear validation errors when values are selected
    if (name === "status" && value) {
      setStatusError("");
    }
    if (name === "severity" && value) {
      setSeverityError("");
    }
    if (name === "type" && value) {
      setIssueTypeError("");
      setStoryError(""); // Clear story error when type changes
      // Reset story selection when issue type changes away from BUG/DEFECT/SUGGESTIONS
      if (value !== "BUG" && value !== "DEFECT" && value !== "SUGGESTIONS") {
        setSelectedStory(null);
        setStoryCreationData({ summary: "", description: "" });
        setStoryTab(null);
      }
    }
    if (name === "project" && value) {
      setProjectError("");
    }
    if (name === "currentSprint" && value) {
      setSprintError("");
    }
  };
  const resetForm = () => {
    setTaskData((p) => ({
      ...defaultTaskData,
      // Reset sprint-related fields
      currentSprint: null,
      isBackLog: false,
    }));
    // Clear all validation errors on reset
    setStatusError("");
    setSeverityError("");
    setIssueTypeError("");
    setProjectError("");
    setSprintError("");
    setTagInput("");
    setAttachments([]);
    setPendingUploads(new Set());
    setOriginalFilenames(new Map());
    setTranscribingVideos(new Set());
    setTranscriptionProgress(new Map());
    setVideoFiles(new Map());
    setIncludeFullPageScreenshot(true); // Reset fullscreen screenshot toggle
    // Reset story link state
    setSelectedStory(null);
    setStoryCreationData({ summary: "", description: "" });
  };

  // Story Link Handlers
  const handleStorySelect = (storyId: string) => {
    setSelectedStory(storyId);
    setStoryError(""); // Clear error when story is selected
  };

  const handleCreateStory = async () => {
    if (!storyCreationData.summary.trim()) {
      toast.error(t("Story summary is required"));
      return;
    }

    setIsCreatingStory(true);
    try {
      // Get default status for the project (first status in the list)
      const defaultStatus = availableStatus?.[0]?._id || "";

      const payload = {
        summary: storyCreationData.summary,
        description: storyCreationData.description || "",
        project: currentProjectId,
        type: "STORY",
        status: defaultStatus,
        severity: "Medium", // Default severity
        tags: [],
        assignees: [],
        pageUrl: pageUrl || "",
      };

      const response = await createBug(payload);
      if (response?.data) {
        const newStoryId = response?.data?.data?.decryptedData?._id;
        toast.success(t("Story created successfully"));
        setSelectedStory(newStoryId);
        setStoryCreationData({ summary: "", description: "" });
        setStoryTab(null); // Close the create form
        // Refetch stories to update the list
        refetchStories();
      } else {
        toast.error(t("Failed to create story"));
      }
    } catch (error) {
      console.error("Error creating story:", error);
      toast.error(t("Failed to create story"));
    } finally {
      setIsCreatingStory(false);
    }
  };

  const handleViewAttachment = (attachment: AttachmentType) => {
    // Check if it's a video by type or fileType
    const isVideo =
      attachment.type === "video" ||
      (attachment.fileType && attachment.fileType.startsWith("video/")) ||
      (attachment.fileName &&
        attachment.fileName.match(/\.(mp4|webm|ogg|mov)$/i));

    if (isVideo && attachment.url) {
      setPreviewVideoUrl(attachment.url);
      setIsVideoPreviewOpen(true);
    } else {
      // Otherwise show the standard attachment view modal
      setSelectedAttachment(attachment);
      setIsAttachmentViewOpen(true);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    const attachment = attachments[index];
    // Remove from pending uploads if it exists
    if (attachment.fileName) {
      setPendingUploads((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.fileName);
        return newSet;
      });
      // Remove from original filenames map
      setOriginalFilenames((prev) => {
        const newMap = new Map(prev);
        newMap.delete(attachment.fileName);
        return newMap;
      });
    }

    // Handle inline attachment index adjustment
    if (inlineAttachmentIndex !== null) {
      if (index === inlineAttachmentIndex) {
        setInlineAttachmentIndex(null);
      } else if (index < inlineAttachmentIndex) {
        setInlineAttachmentIndex(inlineAttachmentIndex - 1);
      }
    }

    setAttachments((prev) => prev.filter((_, i) => i !== index));
    toast.success(t("Attachment removed successfully"));
  };

  // Function to handle removing screenshots
  const handleRemoveScreenshot = () => {
    setTaskData((prev) => ({
      ...prev,
      screenshot: null,
      fullPageScreenshot: null,
      screenShotKey: "",
      fullPageScreenshotKey: "",
    }));
    toast.success(t("Screenshot removed successfully"));
  };

  // Function to handle removing video recording
  const handleRemoveVideoRecording = () => {
    setTaskData((prev) => ({
      ...prev,
      videoRecording: null,
      recordingLink: "",
    }));
    toast.success(t("Video recording removed successfully"));
  };

  // Handle paste from system clipboard (for files)
  const handleSystemPaste = async (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      event.preventDefault();
      await processFiles(files);
      toast.success(t(`${files.length} file(s) pasted from clipboard`));
    }
  };

  const processFiles = async (files: File[]) => {
    setIsUploading(true);

    for (const file of files) {
      const originalFileName = file.name;
      const uniqueFileName = generateUniqueFilename(originalFileName);

      // Store original filename for display purposes
      setOriginalFilenames((prev) =>
        new Map(prev).set(uniqueFileName, originalFileName),
      );

      // Add to pending uploads with unique filename for tracking
      setPendingUploads((prev) => new Set(prev).add(uniqueFileName));

      // Store video file reference for later transcription
      if (isVideoFile(file)) {
        setVideoFiles((prev) => new Map(prev).set(uniqueFileName, file));
      }

      if (!file.type.startsWith("image/")) {
        const newAttachment: AttachmentType = {
          fileType: file.type,
          fileName: uniqueFileName, // Use unique filename for backend
          url: null,
          isUploading: true,
        };
        setAttachments((prev) => [...prev, newAttachment]);

        // For non-image files, we still need to upload them
        try {
          const formData = new FormData();
          formData.append("file", file, uniqueFileName);

          const response = await axios.post(
            `${import.meta.env.VITE_COMPANY_API_URL}/file/upload`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
              withCredentials: true,
            },
          );
          const uploadUrl = response?.data?.data?.localData?.Key || null;

          setAttachments((prev) =>
            prev.map((att) =>
              att.fileName === uniqueFileName && att.url === null
                ? {
                    ...att,
                    serverUrl: uploadUrl,
                    isUploading: false,
                  }
                : att,
            ),
          );
        } catch (error) {
          console.error("Error uploading file:", error);
          setAttachments((prev) =>
            prev.map((att) =>
              att.fileName === uniqueFileName && att.url === null
                ? {
                    ...att,
                    isUploading: false,
                    uploadFailed: true,
                  }
                : att,
            ),
          );
        } finally {
          setPendingUploads((prev) => {
            const newSet = new Set(prev);
            newSet.delete(uniqueFileName);
            return newSet;
          });
        }
        continue;
      }

      const reader = new FileReader();

      reader.onload = async (e) => {
        const base64Data = e.target?.result as string;

        const newAttachment: AttachmentType = {
          fileType: file.type,
          fileName: uniqueFileName, // Use unique filename for backend
          url: base64Data,
          isUploading: true,
        };

        setAttachments((prev) => [...prev, newAttachment]);

        try {
          const uploadUrl = await uploadScreenshot(
            base64Data,
            undefined,
            originalFileName,
          );

          setAttachments((prev) =>
            prev.map((att) =>
              att.fileName === uniqueFileName && att.url === base64Data
                ? {
                    ...att,
                    serverUrl: uploadUrl,
                    isUploading: false,
                  }
                : att,
            ),
          );
        } catch (error) {
          console.error("Error uploading image:", error);
          setAttachments((prev) =>
            prev.map((att) =>
              att.fileName === uniqueFileName && att.url === base64Data
                ? {
                    ...att,
                    isUploading: false,
                    uploadFailed: true,
                  }
                : att,
            ),
          );
        } finally {
          setPendingUploads((prev) => {
            const newSet = new Set(prev);
            newSet.delete(uniqueFileName);
            return newSet;
          });
        }
      };

      reader.onerror = () => {
        console.error("Error reading file");
        toast.error(t("Error reading file"));
        setPendingUploads((prev) => {
          const newSet = new Set(prev);
          newSet.delete(uniqueFileName);
          return newSet;
        });
      };

      reader.readAsDataURL(file);
    }

    setIsUploading(false);
  };

  // Handle video transcription when button is clicked
  const handleTranscribeVideo = async (fileName: string) => {
    const videoFile = videoFiles.get(fileName);
    const originalFileName = originalFilenames.get(fileName) || fileName;

    if (!videoFile) {
      console.error("❌ Video file not found in videoFiles map");
      setTranscriptionProgress((prev) =>
        new Map(prev).set(fileName, "Video file not found ✗"),
      );
      setTimeout(() => {
        setTranscriptionProgress((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileName);
          return newMap;
        });
      }, 3000);
      return;
    }

    // Check if already transcribing
    if (transcribingVideos.has(fileName)) {
      return;
    }

    // Add to transcribing videos set
    setTranscribingVideos((prev) => new Set(prev).add(fileName));
    setTranscriptionProgress((prev) =>
      new Map(prev).set(fileName, "Preparing video"),
    );

    try {
      // Process video transcription with progress updates
      const transcriptionText = await processVideoTranscription(
        videoFile,
        "en",
        (progressMessage) => {
          // Update progress
          setTranscriptionProgress((prev) =>
            new Map(prev).set(fileName, progressMessage),
          );
        },
      );

      // Append transcription to description with proper formatting
      setTaskData((prev) => {
        const hasExistingText = prev.description.trim().length > 0;
        const spacing = hasExistingText ? "\n\n" : "";
        const formattedTranscription = `${spacing}Video Summary\n${transcriptionText}`;

        return {
          ...prev,
          description: prev.description + formattedTranscription,
        };
      });

      // Update progress to complete
      setTranscriptionProgress((prev) =>
        new Map(prev).set(fileName, "Completed ✓"),
      );

      // Clean up progress after 5 seconds
      setTimeout(() => {
        setTranscriptionProgress((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileName);
          return newMap;
        });
      }, 5000);
    } catch (error) {
      console.error("Transcription error:", error);
      setTranscriptionProgress((prev) =>
        new Map(prev).set(fileName, "Failed ✗"),
      );

      // Clean up error progress after 5 seconds
      setTimeout(() => {
        setTranscriptionProgress((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileName);
          return newMap;
        });
      }, 5000);
    } finally {
      // Remove from transcribing set
      setTranscribingVideos((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileName);
        return newSet;
      });
    }
  };

  // Validation function
  const validateForm = () => {
    let hasErrors = false;

    if (!taskData?.summary.trim()) {
      toast.error(t("Please add a summary"));
      hasErrors = true;
    }

    if (!taskData?.project) {
      setProjectError(t("Project is required"));
      hasErrors = true;
    }

    if (!taskData?.severity) {
      setSeverityError(t("Severity is required"));
      hasErrors = true;
    }

    if (!taskData?.status) {
      setStatusError(t("Status is required"));
      hasErrors = true;
    }

    if (!taskData?.type) {
      setIssueTypeError(t("Issue Type is required"));
      hasErrors = true;
    }

    // Validate story link for DEFECT when story is required for this type
    const isDefectType =
      taskData?.type === "BUG" ||
      taskData?.type === "DEFECT" ||
      taskData?.type === "SUGGESTIONS";
    const storyRequiredTypes =
      projectDetails?.data?.decryptedData?.storyRequiredTypes || [];
    const isStoryRequiredForType =
      Array.isArray(storyRequiredTypes) &&
      storyRequiredTypes.includes(taskData?.type);

    if (isDefectType && isStoryRequiredForType && !selectedStory) {
      setStoryError(t("Story is required"));
      hasErrors = true;
    }

    // Validate sprint selection for SCRUM boards with active sprints
    if (shouldShowSprintDropdown && !taskData?.currentSprint) {
      setSprintError(t(""));
    }

    return !hasErrors;
  };

  // Check if there are pending uploads or transcriptions
  const hasPendingUploads = () => {
    return (
      pendingUploads.size > 0 ||
      attachments.some((att) => att.isUploading) ||
      transcribingVideos.size > 0
    );
  };

  const handleButtonClick = async () => {
    if (hasPendingUploads()) {
      toast.warning(
        t(
          "Please wait for all uploads and transcriptions to complete before creating the task",
        ),
      );
      return;
    }

    // Save annotation before creating task (if there's an annotation editor)
    let annotatedImageData: string | null = null;
    let annotatedImageUrl: string | null = null;

    if (
      annotationEditorRef.current &&
      (taskData?.screenshot || taskData?.fullPageScreenshot)
    ) {
      annotatedImageData = annotationEditorRef.current.saveAnnotation();

      // Upload the annotated image to get the URL
      if (annotatedImageData) {
        try {
          const isAnnotatingMainScreenshot = !!taskData?.screenshot;
          const filename = isAnnotatingMainScreenshot
            ? "annotated_screenshot.png"
            : "annotated_fullpage_screenshot.png";

          annotatedImageUrl = await uploadScreenshot(
            annotatedImageData,
            undefined,
            filename,
          );
        } catch (error) {
          console.error("Error uploading annotated image:", error);
          toast.error(t("Failed to upload annotated image"));
          return;
        }
      }
    }

    if (validateForm()) {
      handleSubmit(annotatedImageData, annotatedImageUrl);
    }
  };

  const handleSubmit = async (
    annotatedImage?: string | null,
    annotatedImageUrl?: string | null,
  ) => {
    // Final check to ensure no uploads or transcriptions are pending
    if (hasPendingUploads()) {
      toast.warning(
        t(
          "Please wait for all uploads and transcriptions to complete before creating the task",
        ),
      );
      return;
    }

    // Check if any attachments failed to upload
    const failedUploads = attachments.filter((att) => att.uploadFailed);
    if (failedUploads.length > 0) {
      toast.error(
        t(
          "Some attachments failed to upload. Please remove them or try again.",
        ),
      );
      return;
    }

    const osDetail = await getOperatingSystemInfo();
    setIsLoading(true);
    try {
      const processedAttachments = attachments.map((att) => ({
        ...att,
        url: att.serverUrl || att.url,
        fileName: att.fileName, // This is already the unique filename
        fileType: att.fileType,
      }));

      // Combine days, hours and minutes into estimation string
      const estimation = updateEstimation(
        taskData.estimationDays || "",
        taskData.estimationHours || "",
        taskData.estimationMinutes || "",
      );

      // Sprint payload logic with validation
      let sprintPayload = {};

      if (taskData.boardType === "SCRUM") {
        // Check if currentSprint exists and is valid in the sprints array
        const isSprintValid =
          taskData.currentSprint &&
          sprints?.find((sprint) => sprint._id === taskData.currentSprint);

        if (isSprintValid) {
          // Sprint exists in the list, pass it
          sprintPayload = {
            currentSprint: taskData.currentSprint,
          };
        } else {
          // Sprint doesn't exist or is not selected, set to backlog
          sprintPayload = {
            isBackLog: true,
          };
        }
      }

      // Determine which screenshot field to update with annotated image
      const screenshotOverride: {
        screenshot?: string;
        fullPageScreenshot?: string;
        screenShotKey?: string;
        fullPageScreenshotKey?: string;
      } = {};
      if (annotatedImage && annotatedImageUrl) {
        // Check which screenshot was being annotated
        if (taskData?.screenshot) {
          screenshotOverride.screenshot = annotatedImage;
          screenshotOverride.screenShotKey = annotatedImageUrl;
        } else if (taskData?.fullPageScreenshot) {
          screenshotOverride.fullPageScreenshot = annotatedImage;
          screenshotOverride.fullPageScreenshotKey = annotatedImageUrl;
        }
      }

      const enhancedTaskData = {
        ...taskData,
        projectKey: taskData?.projectKey,
        bugStatus: taskData?.status,
        severity: taskData?.severity,
        jiraPriority: "1",
        jiraStatus: "11",
        isSettings: taskData?.keepSettings,
        pageUrl: pageUrl,
        recordingLink: taskData?.recordingLink,
        videoRecording: taskData?.videoRecording,
        jiraDatas: formData,
        jiraProjectKey: taskData?.jiraProjectKey,
        project: taskData?.project,
        jiraProjectName: taskData.jiraProjectName,
        jiraProjectId: taskData.jiraProjectId,
        startDate: taskData?.startDate,
        dueDate: taskData?.dueDate,
        estimation: estimation,
        deviceDetails: JSON.stringify(osDetail),
        ...sprintPayload,
        ...(selectedEpic ? { epic: selectedEpic } : {}),
        // Include linked story if defect is linked to a story
        ...((taskData?.type === "BUG" ||
          taskData?.type === "DEFECT" ||
          taskData?.type === "SUGGESTIONS") &&
        selectedStory
          ? { associateStoryId: selectedStory }
          : {}),
        // Only include fullPageScreenshot if checkbox is checked
        ...(includeFullPageScreenshot
          ? {}
          : {
              fullPageScreenshot: null,
              fullPageScreenshotKey: "",
            }),
        // Override with annotated image if available
        ...screenshotOverride,
      };

      await onSubmit(enhancedTaskData, processedAttachments, availableUsers);

      // Only refresh the specific column where the task was created
      // taskData.status corresponds to the columnId in a status-grouped board
      if (refreshColumnData && taskData.status) {
        await refreshColumnData(taskData.status);
      }

      // copy link functionality with the same id
      // http://localhost:4173/public/bug/695f7439457ff5284969707b

      // Reset form and close modal after successful creation and refresh
      resetForm();
      onClose();
      setFormData({});
    } catch (error) {
      console.error("Error creating bug", error);
      toast.error(t("Failed to create task. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (files) {
      await processFiles(Array.from(files));
      // Clear the input value so the same file can be selected again
      event.target.value = "";
    }
  };

  const handleAIImprove = async (transcript: string): Promise<string> => {
    if (!isAiConfigured) {
      setShowAiConfigDialog(true);
      return "";
    }
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_CHAT_API_URL}/api/agents/task-refiner/generate-text`,
        {
          prompt: transcript,
          store: false,
          module: "general",
          businessApplication:
            storageService.get("businessApplication") || "123",
          team: storageService.get("team") || "111",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );

      // Extract improved text from response
      if (response?.data?.text) {
        return response.data.text;
      }

      // Return original if no text in response
      return transcript;
    } catch (error) {
      console.error("Error improving text with AI:", error);
      toast.error(t("Failed to improve text with AI"));
      // Return original transcript on error
      return transcript;
    }
  };

  const handleAudioAttachment = async (audioBlob: Blob, transcript: string) => {
    try {
      // Generate unique filename for the audio
      const timestamp = Date.now();
      const uniqueFileName = `voice_recording_${timestamp}.webm`;
      const originalFileName = `voice_recording_${timestamp}.webm`;

      // Store original filename for display
      setOriginalFilenames((prev) =>
        new Map(prev).set(uniqueFileName, originalFileName),
      );

      // Add to pending uploads
      setPendingUploads((prev) => new Set(prev).add(uniqueFileName));

      // Create File from Blob
      const audioFile = new File([audioBlob], uniqueFileName, {
        type: "audio/webm",
      });

      // Add to attachments with uploading state
      const newAttachment: AttachmentType = {
        fileType: "audio/webm",
        fileName: uniqueFileName,
        url: null,
        isUploading: true,
      };
      setAttachments((prev) => [...prev, newAttachment]);

      // Upload the audio file
      const formData = new FormData();
      formData.append("file", audioFile, uniqueFileName);

      const response = await axios.post(
        `${import.meta.env.VITE_COMPANY_API_URL}/file/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );

      const uploadUrl = response?.data?.data?.localData?.Key || null;

      // Update attachment with server URL
      setAttachments((prev) =>
        prev.map((att) =>
          att.fileName === uniqueFileName && att.url === null
            ? {
                ...att,
                serverUrl: uploadUrl,
                isUploading: false,
              }
            : att,
        ),
      );

      // Remove from pending uploads
      setPendingUploads((prev) => {
        const newSet = new Set(prev);
        newSet.delete(uniqueFileName);
        return newSet;
      });

      toast.success(t("Audio recording attached successfully"));
    } catch (error) {
      console.error("Error uploading audio:", error);
      toast.error(t("Failed to upload audio recording"));

      // Mark upload as failed
      setAttachments((prev) =>
        prev.map((att) =>
          att.fileName?.startsWith("voice_recording_") && att.url === null
            ? {
                ...att,
                isUploading: false,
                uploadFailed: true,
              }
            : att,
        ),
      );

      // Remove from pending uploads
      setPendingUploads((prev) => {
        const newSet = new Set(prev);
        Array.from(newSet).forEach((filename) => {
          if (filename.startsWith("voice_recording_")) {
            newSet.delete(filename);
          }
        });
        return newSet;
      });
    }
  };

  const handleAssigneeChange = (newValues) => {
    setTaskData((prev) => ({
      ...prev,
      assignees: newValues.map((user) => user.user?._id || user._id),
    }));
  };

  const handleSaveAnnotation = async (annotatedImageData: string) => {
    try {
      // Determine which screenshot we're annotating (main captured or fullpage)
      const isAnnotatingMainScreenshot = !!taskData?.screenshot;
      const filename = isAnnotatingMainScreenshot
        ? "annotated_screenshot.png"
        : "annotated_fullpage_screenshot.png";

      // Update the appropriate screenshot in state
      if (isAnnotatingMainScreenshot) {
        setTaskData((prev) => ({
          ...prev,
          screenshot: annotatedImageData,
        }));
      } else {
        setTaskData((prev) => ({
          ...prev,
          fullPageScreenshot: annotatedImageData,
        }));
      }

      // Upload annotated image to server
      const url = await uploadScreenshot(
        annotatedImageData,
        undefined,
        filename,
      );

      // Update the appropriate screenshot key
      if (isAnnotatingMainScreenshot) {
        setTaskData((prev) => ({
          ...prev,
          screenShotKey: url,
        }));
      } else {
        setTaskData((prev) => ({
          ...prev,
          fullPageScreenshotKey: url,
        }));
      }
    } catch (error) {
      console.error("Error uploading annotated image:", error);
    } finally {
      setIsAnnotationEditorOpen(false);
    }
  };

  const handleClickHere = () => {
    onClose();
    navigate("/company-settings/settings/integration");
  };

  const handleProjectSelect = async (e) => {
    const selectedProjectKey = e.target.value;
    const selectedProject = jiraProjectList?.find(
      (project) => project.key === selectedProjectKey,
    );

    const selectedProjectName = selectedProject?.projectData?.name || "";
    const selectedProjectId = selectedProject?.projectId || "";

    setTaskData((prev) => ({
      ...prev,
      jiraProjectKey: e.target.value || "",
      jiraProjectName: selectedProjectName,
      jiraProjectId: selectedProjectId,
    }));

    setFormData({});
  };

  // Handle estimation days change
  const handleEstimationDaysChange = (value: string) => {
    const estimation = updateEstimation(
      value,
      taskData.estimationHours || "",
      taskData.estimationMinutes || "",
    );
    setTaskData((prev) => ({
      ...prev,
      estimationDays: value,
      estimation: estimation,
    }));
  };

  // Handle estimation hours change
  const handleEstimationHoursChange = (value: string) => {
    const estimation = updateEstimation(
      taskData.estimationDays || "",
      value,
      taskData.estimationMinutes || "",
    );
    setTaskData((prev) => ({
      ...prev,
      estimationHours: value,
      estimation: estimation,
    }));
  };

  // Handle estimation minutes change
  const handleEstimationMinutesChange = (value: string) => {
    const estimation = updateEstimation(
      taskData.estimationDays || "",
      taskData.estimationHours || "",
      value,
    );
    setTaskData((prev) => ({
      ...prev,
      estimationMinutes: value,
      estimation: estimation,
    }));
  };

  useEffect(() => {
    const initializeScreenshots = async () => {
      try {
        if (screenshotData) {
          const url = await uploadScreenshot(
            screenshotData,
            undefined,
            "element_screenshot.png",
          );
          setTaskData((prev) => ({
            ...prev,
            screenShotKey: url,
          }));
        }

        if (blobDataForFullPage) {
          const url = await uploadScreenshot(
            "",
            blobDataForFullPage,
            "fullpage_screenshot.png",
          );
          setTaskData((prev) => ({
            ...prev,
            fullPageScreenshotKey: url,
          }));
        }
      } catch (error) {
        console.error("Screenshot upload failed:", error);
      }
    };

    if (screenshotData || blobDataForFullPage) {
      initializeScreenshots();
    }
  }, [screenshotData, blobDataForFullPage]);

  useEffect(() => {
    if (Array.isArray(configData) && configData?.length > 0) {
      const findJiraConfigExist = configData.find(
        (config) => config.type === "JIRA",
      );
      if (findJiraConfigExist) {
        setIsJiraConfigured(true);
        setTaskData({
          ...taskData,
          platform: "JIRA",
        });
      } else {
        setIsJiraConfigured(false);
        setTaskData({
          ...taskData,
          platform: "NONE",
        });
      }
    } else {
      setIsJiraConfigured(false);
    }
  }, [configData]);

  useEffect(() => {
    // Move the localStorage read inside the useEffect
    const taskDataKeepdecryptedData = JSON.parse(
      localStorage.getItem("taskDataKeep"),
    );

    let days = "";
    let hours = "";
    let minutes = "";

    // Apply saved settings when modal opens or when id changes
    if (open) {
      // Determine the project ID - always prefer URL id
      const projectId = id || taskDataKeepdecryptedData?.project || "";

      if (
        taskDataKeepdecryptedData &&
        Object.keys(taskDataKeepdecryptedData).length > 0 &&
        taskDataKeepdecryptedData.keepSettings
      ) {
        // Parse existing estimation if it exists

        if (taskDataKeepdecryptedData.estimation) {
          const estimationStr = taskDataKeepdecryptedData.estimation;
          const dayMatch = estimationStr.match(/(\d+)d/);
          const hourMatch = estimationStr.match(/(\d+)h/);
          const minuteMatch = estimationStr.match(/(\d+)m/);

          if (dayMatch) days = dayMatch[1];
          if (hourMatch) hours = hourMatch[1];
          if (minuteMatch) minutes = minuteMatch[1];
        }

        // Find the selected project to get its boardType
        const selectedProject = projectList?.find(
          (project) =>
            project?._id === (id || taskDataKeepdecryptedData?.project),
        );
        const boardType = selectedProject?.boardType || "";

        setTaskData((prev) => ({
          ...prev,
          platform: taskDataKeepdecryptedData.platform || "JIRA",
          assignees: taskDataKeepdecryptedData.assignees || [],
          project: projectId,
          projectKey: taskDataKeepdecryptedData.projectKey || "",
          severity: taskDataKeepdecryptedData.severity || "Medium",
          status: taskDataKeepdecryptedData.status || "",
          tags: taskDataKeepdecryptedData.tags || [],
          type: taskDataKeepdecryptedData.type || "DEFECT",
          keepSettings: taskDataKeepdecryptedData.keepSettings || false,
          jiraProjectKey: taskDataKeepdecryptedData.jiraProjectKey || "",
          jiraProjectName: taskDataKeepdecryptedData.jiraProjectName || "",
          jiraProjectId: taskDataKeepdecryptedData.jiraProjectId || "",
          startDate: taskDataKeepdecryptedData.startDate || "",
          dueDate: taskDataKeepdecryptedData.dueDate || "",
          estimation: taskDataKeepdecryptedData.estimation || "",
          estimationDays: taskDataKeepdecryptedData.estimationDays || days,
          estimationHours: taskDataKeepdecryptedData.estimationHours || hours,
          estimationMinutes:
            taskDataKeepdecryptedData.estimationMinutes || minutes,
          boardType: boardType, // Set boardType from selected project
          currentSprint: taskDataKeepdecryptedData.currentSprint || null,
          isBackLog: taskDataKeepdecryptedData.isBackLog || false,
        }));
      } else {
        // No keepSettings or no saved data - use URL id with defaults
        const selectedProject = projectList?.find(
          (project) => project?._id === projectId,
        );
        const boardType = selectedProject?.boardType || "";

        setTaskData((prev) => ({
          ...prev,
          project: projectId,
          severity: prev.severity || "Medium",
          type: prev.type || "DEFECT",
          boardType: boardType,
        }));
      }
    }
  }, [open, projectList, id]); // Add projectList and id as dependencies

  // Safety-net: ensure project is set from URL id whenever modal is open
  useEffect(() => {
    if (open && id && !taskData.project) {
      const selectedProject = projectList?.find(
        (project: any) => project?._id === id,
      );
      setTaskData((prev) => ({
        ...prev,
        project: id,
        boardType: selectedProject?.boardType || prev.boardType,
      }));
    }
  }, [open, id, taskData.project, projectList]);

  // ✅ NEW: Filter assignees to only include users that are part of the current project
  useEffect(() => {
    if (
      taskData.project &&
      taskData.assignees.length > 0 &&
      availableUsers.length > 0
    ) {
      // Get valid user IDs from availableUsers (which are already filtered by project)
      const validUserIds = availableUsers.map(
        (user) => user.user?._id || user._id,
      );

      // Filter assignees to only include users that exist in the current project
      const filteredAssignees = taskData.assignees.filter((assigneeId) =>
        validUserIds.includes(assigneeId),
      );

      // Only update if there's a difference
      if (filteredAssignees.length !== taskData.assignees.length) {
        setTaskData((prev) => ({
          ...prev,
          assignees: filteredAssignees,
        }));

        // Also update localStorage if keepSettings is enabled
        const taskDataKeepdecryptedData = JSON.parse(
          localStorage.getItem("taskDataKeep"),
        );
        if (
          taskDataKeepdecryptedData &&
          taskDataKeepdecryptedData.keepSettings
        ) {
          const updatedTaskData = {
            ...taskDataKeepdecryptedData,
            assignees: filteredAssignees,
          };
          localStorage.setItem("taskDataKeep", JSON.stringify(updatedTaskData));
        }
      }
    }
  }, [taskData.project, availableUsers, taskData.assignees]);

  useEffect(() => {
    if (boardState?.dueDateDateForCalendarView) {
      const dueDate = new Date(boardState.dueDateDateForCalendarView);
      const formattedDate = dueDate.toISOString().split("T")[0];
      setTaskData((prev) => ({
        ...prev,
        dueDate: formattedDate,
      }));
    }
    if (boardState?.selectedColumnStatus) {
      setTaskData((prev) => ({
        ...prev,
        status: boardState?.selectedColumnStatus,
      }));
    }
  }, [
    boardState?.dueDateDateForCalendarView,
    boardState?.selectedColumnStatus,
  ]);
  useEffect(() => {
    if (open && screenshotData) {
      const initializeScreenshots = async () => {
        setTaskData((prev) => ({
          ...prev,
          screenshot: screenshotData,
          selector: selector,
        }));
      };
      initializeScreenshots();
    }
    if (fullPageScreenshotData) {
      setTaskData((prev) => ({
        ...prev,
        fullPageScreenshot: fullPageScreenshotData,
      }));
    }
  }, [open, screenshotData, selector, fullPageScreenshotData]);

  useEffect(() => {
    const fetchStatusLabels = async () => {
      if (taskData?.jiraProjectKey) {
        try {
          const response = await getProjectStatusLabels({
            keys: [taskData?.jiraProjectKey],
          });
          if (response?.data?.statuses) {
            setStatusList(response.data.statuses);
          }
        } catch (err) {
          console.error("Error fetching status labels:", err);
        }
      }
    };

    fetchStatusLabels();
  }, [taskData.jiraProjectKey]);

  useEffect(() => {
    if (videoRecording) {
      setTaskData((prev) => ({
        ...prev,
        screenshot: null,
        fullPageScreenshot: null,
        screenShotKey: "",
        fullPageScreenshotKey: "",
        recordingLink: "",
        videoRecording: videoRecording,
      }));
    }
  }, [videoRecording, status]);

  useEffect(() => {
    if (jiraProjectList?.length > 0) {
      setTaskData((prev) => ({
        ...prev,
        jiraProjectKey: null,
      }));
    }
  }, [jiraProjectList]);

  useEffect(() => {
    if (jiraProjectList?.length > 0 && defaultFields) {
      const initialFormData = {};
      Object.entries(defaultFields).forEach(([_fieldLabel, fieldData]) => {
        const fieldKey = Object.keys(fieldData)[0];
        initialFormData[fieldKey] = fieldData[fieldKey];
      });
      setFormData(initialFormData);
    }
  }, [jiraProjectList, defaultFields]);

  useEffect(() => {
    if (currentProjectId) {
      // Check if we should preserve status from keepSettings
      const taskDataKeepdecryptedData = JSON.parse(
        localStorage.getItem("taskDataKeep"),
      );
      const shouldPreserveStatus =
        taskDataKeepdecryptedData?.keepSettings &&
        taskDataKeepdecryptedData?.status &&
        taskDataKeepdecryptedData?.project === currentProjectId;

      if (!boardState?.selectedColumnStatus && !shouldPreserveStatus) {
        // Set to first available status instead of clearing
        const firstStatus = availableStatus?.[0]?._id || "";
        setTaskData((prev) => ({
          ...prev,
          status: firstStatus,
        }));
      }
      setStatusError("");
    }
  }, [currentProjectId, boardState?.selectedColumnStatus, availableStatus]);

  const [isAnnotatingAttachment, setIsAnnotatingAttachment] = useState(false);
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(null);

  const handleAnnotateAttachment = (index) => {
    setCurrentAttachmentIndex(index);
    setIsAnnotatingAttachment(true);
  };

  const handleSaveAttachmentAnnotation = async (annotatedImageData) => {
    try {
      const updatedAttachments = [...attachments];
      const attachment = updatedAttachments[currentAttachmentIndex];

      // Get original filename for prefix
      const originalFilename =
        originalFilenames.get(attachment.fileName) || attachment.fileName;

      // Generate unique filename for annotated attachment
      const uniqueFilename = generateUniqueFilename(
        `annotated_${originalFilename}`,
      );

      // Store new mapping for display purposes
      setOriginalFilenames((prev) =>
        new Map(prev).set(uniqueFilename, `annotated_${originalFilename}`),
      );

      updatedAttachments[currentAttachmentIndex] = {
        ...attachment,
        url: annotatedImageData,
        fileName: uniqueFilename, // Use unique filename for backend
        isAnnotated: true,
        isUploading: true,
      };

      setAttachments(updatedAttachments);

      // Upload with unique filename
      const uploadUrl = await uploadScreenshot(
        annotatedImageData,
        undefined,
        `annotated_${originalFilename}`,
      );

      setAttachments((prev) =>
        prev.map((att, idx) =>
          idx === currentAttachmentIndex
            ? {
                ...att,
                serverUrl: uploadUrl,
                isUploading: false,
              }
            : att,
        ),
      );
    } catch (error) {
      console.error("Error uploading annotated image:", error);
      setAttachments((prev) =>
        prev.map((att, idx) =>
          idx === currentAttachmentIndex
            ? {
                ...att,
                isUploading: false,
                uploadFailed: true,
              }
            : att,
        ),
      );
    } finally {
      setIsAnnotatingAttachment(false);
      setCurrentAttachmentIndex(null);
    }
  };

  const handleSaveInlineAttachmentAnnotation = async (
    annotatedImageData: string,
  ) => {
    if (inlineAttachmentIndex === null) return;
    try {
      const updatedAttachments = [...attachments];
      const attachment = updatedAttachments[inlineAttachmentIndex];

      const originalFilename =
        originalFilenames.get(attachment.fileName) || attachment.fileName;

      const uniqueFilename = generateUniqueFilename(
        `annotated_${originalFilename}`,
      );

      setOriginalFilenames((prev) =>
        new Map(prev).set(uniqueFilename, `annotated_${originalFilename}`),
      );

      updatedAttachments[inlineAttachmentIndex] = {
        ...attachment,
        url: annotatedImageData,
        fileName: uniqueFilename,
        isAnnotated: true,
        isUploading: true,
      };

      setAttachments(updatedAttachments);

      const uploadUrl = await uploadScreenshot(
        annotatedImageData,
        undefined,
        `annotated_${originalFilename}`,
      );

      setAttachments((prev) =>
        prev.map((att, idx) =>
          idx === inlineAttachmentIndex
            ? {
                ...att,
                serverUrl: uploadUrl,
                isUploading: false,
              }
            : att,
        ),
      );
    } catch (error) {
      console.error("Error uploading inline annotated image:", error);
      setAttachments((prev) =>
        prev.map((att, idx) =>
          idx === inlineAttachmentIndex
            ? {
                ...att,
                isUploading: false,
                uploadFailed: true,
              }
            : att,
        ),
      );
    }
  };

  const handleBackInlineAttachment = () => {
    setInlineAttachmentIndex(null);
  };

  const handleDoneInlineAttachment = () => {
    if (inlineAnnotationEditorRef.current?.isDirty()) {
      inlineAnnotationEditorRef.current.saveAnnotation();
    }
    setInlineAttachmentIndex(null);
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    return html.replace(/<\/?[^>]+(>|$)/g, "");
  };
  const handleClickDetailView = () => {
    setDetailView(true);
  };
  return (
    <>
      <StyledDialog
        open={open}
        id="my-custom-modal"
        onPaste={handleSystemPaste}
        sx={{
          "& .MuiDialog-paper":
            inlineAttachmentIndex !== null
              ? {}
              : isMaximized
                ? {}
                : hasMediaContent
                  ? {
                      width: "55vw",
                      height: "65vh",
                      maxWidth: "55vw",
                      maxHeight: "65vh",
                    }
                  : {
                      width: "700px",
                      maxWidth: "700px",
                      height: "70vh",
                      maxHeight: "70vh",
                    },
        }}
      >
        {/* Main Container - Full Height Grid */}
        <Box
          sx={{
            display: "grid",

            gridTemplateRows: "auto 1fr",
            height:
              inlineAttachmentIndex !== null
                ? "100%"
                : isMaximized
                  ? "85vh"
                  : hasMediaContent
                    ? "65vh"
                    : "70vh",
            width:
              inlineAttachmentIndex !== null
                ? "100%"
                : isMaximized
                  ? "80vw"
                  : hasMediaContent
                    ? "55vw"
                    : "700px",
            maxHeight:
              inlineAttachmentIndex !== null
                ? "100%"
                : isMaximized
                  ? "85vh"
                  : hasMediaContent
                    ? "65vh"
                    : "70vh",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Top Bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              minHeight: 52,
            }}
          >
            {/* Left - Title */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {inlineAttachmentIndex !== null ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton size="small" onClick={handleBackInlineAttachment}>
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                    {t("Annotate Screenshot")}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {t("Create a New Task")}
                </Typography>
              )}
            </Box>

            {/* Right - Actions */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              {inlineAttachmentIndex !== null ? (
                <Button
                  variant="contained"
                  onClick={handleDoneInlineAttachment}
                  sx={{
                    borderRadius: "20px",
                    textTransform: "none",
                    px: 3,
                  }}
                >
                  {t("Done Annotation")}
                </Button>
              ) : (
                <>
                  <LoadingButton
                    variant="contained"
                    onClick={handleButtonClick}
                    loading={isSubmitting || recordBugLoading || isLoading}
                    disabled={
                      (taskData?.platform === "JIRA" && !taskData?.project) ||
                      !taskData?.summary ||
                      !DEFEKT_TRACKER.CREATE(myPermissions || permissions) ||
                      hasPendingUploads()
                    }
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      px: 3,
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: "none",
                      },
                    }}
                  >
                    {hasPendingUploads() ? t("Uploading...") : t("Create Task")}
                  </LoadingButton>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Tooltip
                      title={isMaximized ? t("Restore") : t("Maximize")}
                      placement="bottom"
                    >
                      <IconButton
                        onClick={() => setIsMaximized(!isMaximized)}
                        size="small"
                        sx={{
                          color: "text.secondary",
                          borderRadius: "6px",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        {isMaximized ? (
                          <CloseFullscreenIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <OpenInFullIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Close")} placement="bottom">
                      <IconButton
                        onClick={handleClose}
                        disabled={isSubmitting || recordBugLoading || isLoading}
                        size="small"
                        sx={{
                          color: "text.secondary",
                          borderRadius: "6px",
                          "&:hover": {
                            backgroundColor: "action.hover",
                            color: "error.main",
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* Main Content - Conditional Layout */}
          <Allotment
            defaultSizes={isMaximized ? [65, 35] : [50, 50]}
            minSize={
              inlineAttachmentIndex !== null
                ? undefined
                : isMaximized
                  ? 200
                  : 150
            }
            separator={inlineAttachmentIndex === null}
          >
            {/* Left Panel - Always visible, shows media or form fields */}
            <Allotment.Pane
              preferredSize={
                inlineAttachmentIndex !== null
                  ? "100%"
                  : isMaximized
                    ? "70%"
                    : "50%"
              }
              minSize={isMaximized ? 250 : 310}
              maxSize={isMaximized ? 1400 : 390}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateRows: hasMediaContent ? "1fr auto" : "1fr",
                  backgroundColor: hasMediaContent
                    ? "action.hover"
                    : "background.paper",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  minHeight: 0,
                }}
              >
                {/* Content Area */}
                {inlineAttachmentIndex !== null &&
                attachments[inlineAttachmentIndex] ? (
                  // Inline Attachment Annotation View - Full width
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateRows: "1fr",
                      overflow: "hidden",
                      minHeight: 0,
                    }}
                  >
                    <AnnotationEditor
                      ref={inlineAnnotationEditorRef}
                      open={true}
                      onClose={handleBackInlineAttachment}
                      imageUrl={attachments[inlineAttachmentIndex].url}
                      onSave={handleSaveInlineAttachmentAnnotation}
                      embedded={true}
                      toolbarPosition="top"
                    />
                  </Box>
                ) : taskData?.videoRecording ? (
                  // Video Recording View
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      p: 2,
                    }}
                  >
                    <video
                      src={taskData?.videoRecording}
                      controls
                      style={{ maxWidth: "100%", maxHeight: "100%" }}
                    />
                  </Box>
                ) : taskData?.screenshot || taskData?.fullPageScreenshot ? (
                  // Annotation Editor - Embedded Directly
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateRows: "1fr",
                      overflow: "hidden",
                      minHeight: 0,
                    }}
                  >
                    <AnnotationEditor
                      ref={annotationEditorRef}
                      open={true}
                      onClose={() => {}}
                      imageUrl={
                        taskData?.screenshot ||
                        taskData?.fullPageScreenshot ||
                        ""
                      }
                      onSave={handleSaveAnnotation}
                      embedded={true}
                    />
                  </Box>
                ) : recordBugLoading ? (
                  // Loading State
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      gap: 2,
                    }}
                  >
                    <CircularProgress />
                    <Typography color="text.secondary">
                      {t("Processing Video...")}
                    </Typography>
                  </Box>
                ) : null}

                {/* Bottom Bar - Fullscreen checkbox and Actions (only show when media exists, hide during inline attachment annotation) */}
                {hasMediaContent && inlineAttachmentIndex === null && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: "background.paper",
                      borderTop: "1px solid",
                      borderColor: "divider",
                      p: 1.5,
                      minHeight: 56,
                    }}
                  >
                    {/* Fullscreen Screenshot Checkbox */}
                    {taskData?.screenshot && taskData?.fullPageScreenshot && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Checkbox
                          checked={includeFullPageScreenshot}
                          onChange={(e) =>
                            setIncludeFullPageScreenshot(e.target.checked)
                          }
                          size="small"
                        />
                        <Box
                          sx={{
                            width: 40,
                            height: 30,
                            borderRadius: "4px",
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: includeFullPageScreenshot
                              ? "primary.main"
                              : "divider",
                            opacity: includeFullPageScreenshot ? 1 : 0.5,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setSelectedImage(taskData?.fullPageScreenshot);
                            setIsImagePreviewOpen(true);
                          }}
                        >
                          <img
                            src={taskData?.fullPageScreenshot}
                            alt={t("Full page screenshot")}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {t("Also include fullscreen screenshot")}
                        </Typography>
                        <Tooltip
                          title={t(
                            "Include a fullscreen screenshot along with the captured area",
                          )}
                        >
                          <InfoOutlinedIcon
                            sx={{ fontSize: 16, color: "text.disabled" }}
                          />
                        </Tooltip>
                      </Box>
                    )}
                    {!(
                      taskData?.screenshot && taskData?.fullPageScreenshot
                    ) && <Box />}

                    {/* Right side actions */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* File Upload */}
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*,.mp4,.webm,.mov,.ogg"
                        id="file-upload-bottom"
                        style={{ display: "none" }}
                        onChange={handleFileUpload}
                      />
                      <label htmlFor="file-upload-bottom">
                        <Tooltip title={t("Attach files")}>
                          <IconButton
                            component="span"
                            size="small"
                            sx={{ color: "text.secondary" }}
                          >
                            <AttachFileIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </label>
                    </Box>
                  </Box>
                )}

                {/* Left Column Form Fields - show when no media content */}
                {!hasMediaContent && activeTab === "1" && (
                  <Box
                    sx={{
                      minHeight: 0,
                      overflow: "auto",
                      p: 2.5,
                    }}
                  >
                    {/* Summary */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ position: "relative" }}>
                        {/* Processing Message Overlay for Summary */}
                        {summaryProcessingMessage && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              backgroundColor: "rgba(255, 255, 255, 0.98)",
                              padding: "6px 12px",
                              borderRadius: "16px",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
                              zIndex: 10,
                              border: "1px solid #e2e8f0",
                              pointerEvents: "none",
                            }}
                          >
                            <CircularProgress size={14} thickness={4} />
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#475569",
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {summaryProcessingMessage}
                            </Typography>
                          </Box>
                        )}
                        <TextField
                          placeholder={t("Add summary")}
                          name="summary"
                          value={taskData?.summary}
                          onChange={handleInputChange}
                          variant="outlined"
                          fullWidth
                          size="small"
                          label={t("Summary")}
                          required
                          InputLabelProps={{
                            sx: {
                              "& .MuiInputLabel-asterisk": {
                                color: theme.palette.error.main,
                              },
                            },
                          }}
                          InputProps={{
                            sx: {
                              "& input": {
                                color:
                                  isVoiceRecording || isVoiceFinalizing
                                    ? "transparent"
                                    : "inherit",
                              },
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <SummaryVoiceRecorder
                                  onTranscriptChange={(transcript) => {
                                    setTaskData((prev) => ({
                                      ...prev,
                                      summary: transcript,
                                    }));
                                  }}
                                  onAIImprove={handleAIImprove}
                                  currentText={taskData?.summary || ""}
                                  maxLength={250}
                                  language="en-US"
                                  onRecordingChange={setIsVoiceRecording}
                                  onFinalizingChange={setIsVoiceFinalizing}
                                  onProcessingMessageChange={
                                    setSummaryProcessingMessage
                                  }
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.disabled",
                          fontSize: "0.7rem",
                          mt: 0.5,
                          display: "block",
                        }}
                      >
                        {taskData?.summary?.length || 0} / 250
                      </Typography>
                    </Box>

                    {/* Description */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ position: "relative" }}>
                        <Box
                          sx={{
                            height: "220px",
                            maxHeight: "220px",
                            overflow: "hidden",
                            borderRadius: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            "& .editor-container": {
                              height: "100% !important",
                              maxHeight: "100% !important",
                            },
                            "& .quill": {
                              height: "100% !important",
                              display: "flex !important",
                              flexDirection: "column !important",
                            },
                            "& .ql-container": {
                              flex: "1 !important",
                              overflow: "auto !important",
                            },
                            "& .ql-editor": {
                              height: "auto !important",
                              maxHeight: "none !important",
                              overflow: "auto !important",
                              opacity: isDescVoiceRecording ? 0.3 : 1,
                            },
                          }}
                        >
                          <RichTextEditor
                            onChange={handleRichTextEditor}
                            value={taskData?.description}
                            placeholder={t("Enter your description here...")}
                            readOnly={false}
                            // @ts-ignore
                            style={{ header: false }}
                            enableVoiceRecording={true}
                            onAudioAttachment={handleAudioAttachment}
                            onAIImprove={handleAIImprove}
                            maxLength={2000}
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled", fontSize: "0.7rem" }}
                        >
                          {stripHtmlTags(taskData?.description)?.length || 0} /
                          2000
                        </Typography>
                      </Box>
                    </Box>

                    {/* Recording Link - Hidden for now */}
                    {/* <StyledTextField
                      placeholder={t("Recording Link")}
                      name="recordingLink"
                      value={recordedURL || taskData?.recordingLink}
                      onChange={handleInputChange}
                      variant="outlined"
                      fullWidth
                      sx={{ mb: 2 }}
                      disabled={recordBugLoading || recordedURL ? true : false}
                      label={t("Recording Link")}
                      size="small"
                    /> */}

                    {/* File Upload Drop Zone */}
                    <DropZone
                      className={isDragOver ? "drag-over" : ""}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() =>
                        document.getElementById("file-upload-form")?.click()
                      }
                      sx={{ mb: 2 }}
                    >
                      <Box
                        className="dropzone-icon"
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? theme.palette.primary.main + "20"
                              : theme.palette.primary.main + "10",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.25s ease",
                        }}
                      >
                        <CloudUploadIcon
                          sx={{
                            fontSize: 24,
                            color: "primary.main",
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "text.primary",
                          }}
                        >
                          {t("Drag & drop files here")}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontSize: "0.65rem" }}
                        >
                          {t("or")}{" "}
                          <Box
                            component="span"
                            sx={{
                              color: "primary.main",
                              fontWeight: 600,
                              textDecoration: "underline",
                              textUnderlineOffset: "2px",
                            }}
                          >
                            {t("click to browse")}
                          </Box>
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.disabled",
                            fontSize: "0.6rem",
                            mt: 0.5,
                          }}
                        >
                          {t("Images & Videos supported")}
                        </Typography>
                      </Box>
                    </DropZone>
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*,.mp4,.webm,.mov,.ogg"
                      id="file-upload-form"
                      style={{ display: "none" }}
                      onChange={handleFileUpload}
                    />

                    {/* Attachments Section */}
                    {attachments.length > 0 && (
                      <Accordion
                        defaultExpanded
                        sx={{
                          mt: 2,
                          boxShadow: "none",
                          "&:before": { display: "none" },
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "10px !important",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            "& .MuiAccordionSummary-content": {
                              margin: "12px 0",
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            {t("Attachments")}
                            {attachments && attachments.length > 0
                              ? ` (${attachments.length})`
                              : ""}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5 }}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, 1fr)",
                              gap: 1.5,
                            }}
                          >
                            {attachments.map((attachment, index) => {
                              // Determine if attachment is an image
                              const isImage =
                                attachment.fileType?.startsWith("image/") ||
                                attachment.fileName
                                  ?.toLowerCase()
                                  .match(/\.(jpg|jpeg|png|gif|bmp|webp)$/);
                              // Get display name (original filename or fallback to stored filename)
                              const fullName =
                                originalFilenames.get(attachment.fileName) ||
                                attachment.fileName;
                              // Truncate long names: show start...extension
                              const truncateName = (
                                name: string,
                                maxLen = 20,
                              ) => {
                                if (!name || name.length <= maxLen) return name;
                                const extIndex = name.lastIndexOf(".");
                                if (extIndex === -1)
                                  return name.slice(0, maxLen - 3) + "...";
                                const ext = name.slice(extIndex); // e.g. ".png"
                                const basePart = name.slice(
                                  0,
                                  maxLen - ext.length - 3,
                                );
                                return basePart + "..." + ext;
                              };
                              const displayName = truncateName(fullName);

                              return (
                                <Box
                                  key={index}
                                  sx={{
                                    bgcolor: "background.paper",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                  }}
                                >
                                  {/* Thumbnail */}
                                  <Box
                                    sx={{
                                      height: 100,
                                      overflow: "hidden",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      isImage
                                        ? setInlineAttachmentIndex(index)
                                        : handleViewAttachment(attachment)
                                    }
                                  >
                                    <AttachmentPreview
                                      attachment={attachment}
                                    />
                                  </Box>

                                  {/* Footer: name + actions */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      px: 1,
                                      py: 0.5,
                                      borderTop: "1px solid #e0e0e0",
                                      minHeight: 36,
                                    }}
                                  >
                                    <Tooltip title={fullName} placement="top">
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 500,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          flex: 1,
                                          mr: 0.5,
                                          fontSize: "11px",
                                        }}
                                      >
                                        {displayName}
                                        {attachment.isAnnotated && (
                                          <Typography
                                            component="span"
                                            sx={{
                                              ml: 0.5,
                                              color: "primary.main",
                                              bgcolor: (theme) =>
                                                hexToRgba(
                                                  theme.palette.primary.main,
                                                  0.1,
                                                ),
                                              px: 0.5,
                                              py: 0.25,
                                              borderRadius: "4px",
                                              fontSize: "9px",
                                            }}
                                          >
                                            {t("Annotated")}
                                          </Typography>
                                        )}
                                      </Typography>
                                    </Tooltip>
                                    <Box
                                      sx={{ display: "flex", flexShrink: 0 }}
                                    >
                                      {isImage && (
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            setInlineAttachmentIndex(index)
                                          }
                                          sx={{ color: "primary.main", p: 0.5 }}
                                          disabled={attachment.isUploading}
                                        >
                                          <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      )}
                                      {/* Transcribe button for video files */}
                                      {attachment.fileType?.startsWith(
                                        "video/",
                                      ) && (
                                        <Tooltip
                                          title={
                                            transcribingVideos.has(
                                              attachment.fileName,
                                            )
                                              ? t("Transcribing...")
                                              : t("Generate text from video")
                                          }
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleTranscribeVideo(
                                                attachment.fileName,
                                              )
                                            }
                                            sx={{
                                              color: (() => {
                                                const progress =
                                                  transcriptionProgress.get(
                                                    attachment.fileName,
                                                  );
                                                const isTranscribing =
                                                  transcribingVideos.has(
                                                    attachment.fileName,
                                                  );
                                                const isComplete =
                                                  progress?.includes("✓");

                                                if (isComplete) {
                                                  return "#4caf50"; // Light green for completed
                                                } else if (isTranscribing) {
                                                  return "primary.main"; // Theme primary color when transcribing
                                                }
                                                return "primary.main"; // Default primary color
                                              })(),
                                              backgroundColor: (() => {
                                                const progress =
                                                  transcriptionProgress.get(
                                                    attachment.fileName,
                                                  );
                                                const isTranscribing =
                                                  transcribingVideos.has(
                                                    attachment.fileName,
                                                  );
                                                const isComplete =
                                                  progress?.includes("✓");

                                                if (isComplete) {
                                                  return "rgba(76, 175, 80, 0.1)"; // Light green background
                                                } else if (isTranscribing) {
                                                  return "rgba(25, 118, 210, 0.1)"; // Primary color with 0.1 opacity
                                                }
                                                return "transparent";
                                              })(),
                                              p: 0.5,
                                              borderRadius: 1,
                                              "&:hover": {
                                                backgroundColor: (() => {
                                                  const progress =
                                                    transcriptionProgress.get(
                                                      attachment.fileName,
                                                    );
                                                  const isComplete =
                                                    progress?.includes("✓");
                                                  if (isComplete) {
                                                    return "rgba(76, 175, 80, 0.2)";
                                                  }
                                                  return "rgba(25, 118, 210, 0.2)";
                                                })(),
                                              },
                                            }}
                                            disabled={
                                              attachment.isUploading ||
                                              transcribingVideos.has(
                                                attachment.fileName,
                                              )
                                            }
                                          >
                                            {transcribingVideos.has(
                                              attachment.fileName,
                                            ) ? (
                                              <CircularProgress
                                                size={16}
                                                thickness={4}
                                                sx={{ color: "primary.main" }}
                                              />
                                            ) : (
                                              <SubtitlesIcon
                                                sx={{ fontSize: 16 }}
                                              />
                                            )}
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleRemoveAttachment(index)
                                        }
                                        title={t("Delete attachment")}
                                        sx={{ p: 0.5 }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  {/* Transcription progress indicator */}
                                  {transcriptionProgress.has(
                                    attachment.fileName,
                                  ) && (
                                    <Box
                                      sx={{
                                        px: 1,
                                        pb: 0.5,
                                        borderTop: "1px solid #e0e0e0",
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color={(() => {
                                          const progress =
                                            transcriptionProgress.get(
                                              attachment.fileName,
                                            );
                                          if (progress?.includes("✓")) {
                                            return "success.main";
                                          } else if (progress?.includes("✗")) {
                                            return "error.main";
                                          }
                                          return "primary.main";
                                        })()}
                                        sx={(theme) => ({
                                          display: "inline-block",
                                          fontSize: 11,
                                          fontWeight: 500,
                                          px: 1,
                                          py: 0.5,
                                          borderRadius: 0.5,
                                          backgroundColor: (() => {
                                            const progress =
                                              transcriptionProgress.get(
                                                attachment.fileName,
                                              );
                                            const isComplete =
                                              progress?.includes("✓");
                                            const isFailed =
                                              progress?.includes("✗");
                                            const isInProgress =
                                              !isComplete && !isFailed;
                                            return isInProgress
                                              ? `${theme.palette.primary.main}1a`
                                              : "transparent";
                                          })(),
                                        })}
                                      >
                                        {transcriptionProgress.get(
                                          attachment.fileName,
                                        )}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                )}
              </Box>
            </Allotment.Pane>

            {/* Right Panel - Form Fields (30% when maximized, 50% when not maximized) */}
            {inlineAttachmentIndex === null && (
              <Allotment.Pane
                preferredSize={isMaximized ? "35%" : "50%"}
                minSize={isMaximized ? 250 : 310}
                maxSize={isMaximized ? 1100 : 390}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateRows: "1fr",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.background.paper
                        : theme.palette.grey[50],
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    minHeight: 0,
                    height: "100%",
                  }}
                >
                  {/* Scrollable Form Content */}
                  <Box
                    sx={{
                      minHeight: 0,
                      overflow: "auto",
                      px: 2.5,
                      py: 2,
                    }}
                  >
                    {activeTab === "1" && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            color: "text.secondary",
                            mb: 1.5,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          {t("Details")}
                        </Typography>
                        <Box>
                          <FormControl fullWidth error={!!projectError}>
                            <InputLabel
                              sx={{
                                transform: "translate(14px, 9px) scale(1)",
                                "&.Mui-focused, &.MuiFormLabel-filled": {
                                  transform:
                                    "translate(14px, -9px) scale(0.75)",
                                },
                              }}
                            >
                              {t("Project")}
                              <Box
                                component="span"
                                sx={{ color: theme.palette.error.main }}
                              >
                                *
                              </Box>
                            </InputLabel>
                            <StyledSelect
                              value={taskData?.project || ""}
                              onChange={handleSelectChange}
                              name="project"
                              fullWidth
                              size="small"
                              label={t("Project")}
                              error={!!projectError}
                            >
                              {projectList?.map((option) => (
                                <MenuItem key={option._id} value={option._id}>
                                  {option.name}
                                </MenuItem>
                              ))}
                            </StyledSelect>
                            {projectError && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5 }}
                              >
                                {projectError}
                              </Typography>
                            )}
                          </FormControl>

                          {/* {jiraProjectList?.length > 0 && isAta && (
                        <Box
                          display={"flex"}
                          gap={1}
                          alignItems={"center"}
                          alignContent={"center"}
                        >
                          <FormControl fullWidth>
                            <InputLabel
                              sx={{
                                transform: "translate(14px, 9px) scale(1)",
                                "&.Mui-focused, &.MuiFormLabel-filled": {
                                  transform:
                                    "translate(14px, -9px) scale(0.75)",
                                },
                              }}
                            >
                              {t("Jira Project")}
                            </InputLabel>
                            <StyledSelect
                              value={taskData.jiraProjectKey || ""}
                              onChange={(e) => {
                                handleProjectSelect(e);
                              }}
                              name="jiraProjectKey"
                              disabled={
                                !isJiraConfigured ||
                                isProjectLoading ||
                                !taskData?.project
                              }
                              size="small"
                              label={t("Jira Project")}
                            >
                              <MenuItem disabled value="">
                                {isProjectLoading
                                  ? t("Checking JIRA configuration...")
                                  : isJiraConfigured
                                    ? t("Select Jira Project")
                                    : t("Select Jira Project")}
                              </MenuItem>
                              {isProjectLoading ? (
                                <MenuItem disabled>
                                  {t("Loading projects")}
                                </MenuItem>
                              ) : (
                                Array.isArray(jiraProjectList) &&
                                jiraProjectList?.map((project) => (
                                  <MenuItem
                                    key={project.key}
                                    value={project.key}
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      width={"100%"}
                                    >
                                      <Avatar
                                        src={
                                          project?.projectData?.avatarUrls[
                                            "48x48"
                                          ]
                                        }
                                        sx={{ width: 24, height: 24, mr: 1 }}
                                      />
                                      <Typography>
                                        {project?.projectData?.name} (
                                        {project?.projectData?.key})
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))
                              )}
                            </StyledSelect>
                          </FormControl>
                        </Box>
                      )}

                      {!isJiraConfigured && !isProjectLoading && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ display: "block", mb: 2, mt: -1 }}
                        >
                          {t(
                            "JIRA integration is not configured or does not exist any project. Click",
                          )}{" "}
                          <span
                            onClick={() => {
                              handleClickHere();
                            }}
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color: "secondary.main",
                            }}
                          >
                            {t("here")}
                          </span>{" "}
                          {t("to integrate.")}
                        </Typography>
                      )} */}
                          <Box sx={{ mb: 2 }}>
                            <CustomAutocomplete
                              options={availableUsers || []}
                              value={
                                availableUsers?.filter((user) =>
                                  taskData.assignees.includes(
                                    user.user?._id || user._id,
                                  ),
                                ) || []
                              }
                              onChange={handleAssigneeChange}
                              placeholder={t("Select Assignee(s)")}
                              label={t("Assignee")}
                              disabled={
                                !taskData?.project ||
                                availableUsers.length === 0
                              }
                              multiple={true}
                              isAssignee={true}
                              projectId={taskData?.project}
                            />
                          </Box>

                          <FormControl
                            fullWidth
                            error={!!severityError}
                            sx={{ mb: 2 }}
                          >
                            <InputLabel
                              sx={{
                                transform: "translate(14px, 9px) scale(1)",
                                "&.Mui-focused, &.MuiFormLabel-filled": {
                                  transform:
                                    "translate(14px, -9px) scale(0.75)",
                                },
                              }}
                            >
                              {t("Severity")}
                              <Box
                                component="span"
                                sx={{ color: theme.palette.error.main }}
                              >
                                *
                              </Box>
                            </InputLabel>
                            <Select
                              id="demo-simple-select"
                              value={taskData?.severity}
                              onChange={handleSelectChange}
                              name="severity"
                              size="small"
                              label={t("Severity")}
                              sx={{
                                backgroundColor: theme.palette.background.paper,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: theme.palette.divider,
                                },
                              }}
                              error={!!severityError}
                            >
                              {severityOptions.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    width={"100%"}
                                  >
                                    <ListItemIcon
                                      sx={{
                                        minWidth: "auto",
                                        minHeight: "auto",
                                        width: 20,
                                        height: 20,
                                      }}
                                    >
                                      {option.icon}
                                    </ListItemIcon>
                                    {option.label}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                            {severityError && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5 }}
                              >
                                {severityError}
                              </Typography>
                            )}
                          </FormControl>
                          <FormControl
                            fullWidth
                            error={!!statusError}
                            sx={{ mb: 2 }}
                          >
                            <InputLabel
                              sx={{
                                transform: "translate(14px, 9px) scale(1)",
                                "&.Mui-focused, &.MuiFormLabel-filled": {
                                  transform:
                                    "translate(14px, -9px) scale(0.75)",
                                },
                              }}
                            >
                              {t("Status")}
                              <Box
                                component="span"
                                sx={{ color: theme.palette.error.main }}
                              >
                                *
                              </Box>
                            </InputLabel>
                            <Select
                              value={taskData.status}
                              onChange={handleSelectChange}
                              name="status"
                              disabled={!taskData.project} // Only disable when no project is selected
                              size="small"
                              label={t("Status")}
                              sx={{
                                backgroundColor: theme.palette.background.paper,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: theme.palette.divider,
                                },
                              }}
                              error={!!statusError}
                            >
                              {isLoadingStatusLabels ? (
                                <MenuItem disabled>
                                  {t("Loading Statuses")}
                                </MenuItem>
                              ) : (
                                availableStatus?.map((option) => (
                                  <MenuItem key={option._id} value={option._id}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 12,
                                          height: 12,
                                          backgroundColor:
                                            option.color ||
                                            option.bgColor ||
                                            "#f44336",
                                          borderRadius: "2px",
                                        }}
                                      />
                                      {option.status}
                                    </Box>
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                            {statusError && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5 }}
                              >
                                {statusError}
                              </Typography>
                            )}
                          </FormControl>

                          <FormControl
                            fullWidth
                            error={!!issueTypeError}
                            sx={{ mb: 2 }}
                          >
                            <InputLabel
                              sx={{
                                transform: "translate(14px, 9px) scale(1)",
                                "&.Mui-focused, &.MuiFormLabel-filled": {
                                  transform:
                                    "translate(14px, -9px) scale(0.75)",
                                },
                              }}
                            >
                              {t("Issue Type")}
                              <Box
                                component="span"
                                sx={{ color: theme.palette.error.main }}
                              >
                                *
                              </Box>
                            </InputLabel>
                            <Select
                              id="demo-simple-select"
                              value={taskData?.type}
                              onChange={handleSelectChange}
                              name="type"
                              size="small"
                              label={t("Issue Type")}
                              renderValue={(selected) => {
                                const selectedOption = issueTypeItems.find(
                                  (opt: any) =>
                                    (opt?.value || opt?.code) === selected,
                                );
                                return (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    {selectedOption?.icon ||
                                      getIssueTypeIcon(selected as string)}
                                    {selectedOption?.label || selected}
                                  </Box>
                                );
                              }}
                              sx={{
                                backgroundColor: theme.palette.background.paper,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: theme.palette.divider,
                                },
                              }}
                              error={!!issueTypeError}
                            >
                              {issueTypeItems.map((option) => (
                                <MenuItem
                                  key={option?.value || option?.code}
                                  value={option?.value || option?.code}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    width={"100%"}
                                  >
                                    {option.icon ||
                                      getIssueTypeIcon(
                                        option?.value || option?.code,
                                      )}
                                    {option.label}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                            {issueTypeError && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5 }}
                              >
                                {issueTypeError}
                              </Typography>
                            )}
                          </FormControl>

                          {/* Story Link Section - Show when issue type is DEFECT/BUG */}
                          {shouldShowStoryLink && (
                            <Box sx={{ mb: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mb: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: "4px",
                                      backgroundColor: "#63BA3C",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <BookmarkIcon
                                      sx={{
                                        fontSize: 13,
                                        color: "#ffffff",
                                        fill: "#ffffff",
                                      }}
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {t("Link to Story")}
                                      {(
                                        projectDetails?.data?.decryptedData
                                          ?.storyRequiredTypes || []
                                      ).includes(taskData?.type) && (
                                        <Box
                                          component="span"
                                          sx={{
                                            color: theme.palette.error.main,
                                            ml: 0.5,
                                          }}
                                        >
                                          *
                                        </Box>
                                      )}
                                    </Typography>
                                    <Tooltip
                                      title={t(
                                        "You can make this field required or optional from Project Settings",
                                      )}
                                      arrow
                                      placement="top"
                                    >
                                      <InfoOutlinedIcon
                                        sx={{
                                          fontSize: 12,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                    {!(
                                      projectDetails?.data?.decryptedData
                                        ?.storyRequiredTypes || []
                                    ).includes(taskData?.type) && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        ({t("Optional")})
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                {!storyTab && (
                                  <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setStoryTab("create")}
                                    sx={{ textTransform: "none" }}
                                  >
                                    {t("Create New")}
                                  </Button>
                                )}
                              </Box>

                              {!storyTab ? (
                                <>
                                  <Autocomplete
                                    options={stories}
                                    loading={isStoriesFetching}
                                    value={
                                      stories.find(
                                        (s) => s.id === selectedStory,
                                      ) || null
                                    }
                                    onChange={(_event, newValue) => {
                                      handleStorySelect(
                                        newValue ? newValue.id : null,
                                      );
                                    }}
                                    getOptionLabel={(option) => option.summary}
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder={t(
                                          "Search and select a story...",
                                        )}
                                        size="small"
                                        InputProps={{
                                          ...params.InputProps,
                                          startAdornment: (
                                            <>
                                              {selectedStory ? (
                                                <Box
                                                  sx={{
                                                    ml: 1,
                                                    mr: 0.5,
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: "3px",
                                                    backgroundColor: "#63BA3C",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                  }}
                                                >
                                                  <BookmarkIcon
                                                    sx={{
                                                      fontSize: 12,
                                                      color: "#ffffff",
                                                    }}
                                                  />
                                                </Box>
                                              ) : (
                                                <SearchIcon
                                                  sx={{
                                                    ml: 1,
                                                    mr: 0.5,
                                                    color:
                                                      theme.palette.text
                                                        .secondary,
                                                    fontSize: 20,
                                                  }}
                                                />
                                              )}
                                              {params.InputProps.startAdornment}
                                            </>
                                          ),
                                          endAdornment: (
                                            <>
                                              {isStoriesFetching ? (
                                                <CircularProgress
                                                  color="inherit"
                                                  size={20}
                                                />
                                              ) : null}
                                              {params.InputProps.endAdornment}
                                            </>
                                          ),
                                        }}
                                        sx={{
                                          backgroundColor:
                                            theme.palette.background.paper,
                                          "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                              borderColor:
                                                theme.palette.divider,
                                            },
                                          },
                                        }}
                                      />
                                    )}
                                    renderOption={(props, option) => {
                                      const getSeverityIcon = (
                                        severity: string,
                                      ) => {
                                        switch (severity) {
                                          case "Highest":
                                            return <HighestIcon />;
                                          case "High":
                                            return <HighIcon />;
                                          case "Medium":
                                            return <MediumIcon />;
                                          case "Low":
                                            return <LowIcon />;
                                          default:
                                            return <MediumIcon />;
                                        }
                                      };

                                      return (
                                        <Box
                                          component="li"
                                          {...props}
                                          key={option.id}
                                        >
                                          <Box sx={{ width: "100%" }}>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                mb: 0.5,
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  width: 18,
                                                  height: 18,
                                                  borderRadius: "3px",
                                                  backgroundColor: "#63BA3C",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  flexShrink: 0,
                                                }}
                                              >
                                                <BookmarkIcon
                                                  sx={{
                                                    fontSize: 12,
                                                    color: "#ffffff",
                                                  }}
                                                />
                                              </Box>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  flex: 1,
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                {option.summary}
                                              </Typography>
                                            </Box>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                ml: 3.5,
                                                mt: 0.5,
                                              }}
                                            >
                                              {option.severity && (
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      width: 14,
                                                      height: 14,
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      "& svg": {
                                                        width: 14,
                                                        height: 14,
                                                      },
                                                    }}
                                                  >
                                                    {getSeverityIcon(
                                                      option.severity,
                                                    )}
                                                  </Box>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      fontSize: "0.7rem",
                                                      color:
                                                        theme.palette.text
                                                          .secondary,
                                                    }}
                                                  >
                                                    {option.severity}
                                                  </Typography>
                                                </Box>
                                              )}
                                              {option.status && (
                                                <Chip
                                                  label={
                                                    option.status.name ||
                                                    option.status
                                                  }
                                                  size="small"
                                                  sx={{
                                                    height: 20,
                                                    fontSize: "0.7rem",
                                                    backgroundColor:
                                                      option.status.bgColor ||
                                                      theme.palette.grey[200],
                                                    color:
                                                      option.status.color ||
                                                      theme.palette.text
                                                        .primary,
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          </Box>
                                        </Box>
                                      );
                                    }}
                                    noOptionsText={
                                      <Box sx={{ textAlign: "center", py: 1 }}>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ mb: 1 }}
                                        >
                                          {t("No stories found")}
                                        </Typography>
                                        <Button
                                          size="small"
                                          startIcon={<AddIcon />}
                                          onClick={() => setStoryTab("create")}
                                          variant="outlined"
                                        >
                                          {t("Create New Story")}
                                        </Button>
                                      </Box>
                                    }
                                    loadingText={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: 1,
                                          py: 2,
                                        }}
                                      >
                                        <CircularProgress size={20} />
                                        <Typography variant="body2">
                                          {t("Loading stories...")}
                                        </Typography>
                                      </Box>
                                    }
                                    ListboxProps={{
                                      style: { maxHeight: "300px" },
                                    }}
                                  />

                                  {selectedStory && (
                                    <Box
                                      sx={{
                                        mt: 1.5,
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        border: `1px solid ${hexToRgba(theme.palette.primary.main, 0.1)}`,
                                        backgroundColor: hexToRgba(
                                          theme.palette.primary.main,
                                          0.1,
                                        ),
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1.5,
                                      }}
                                    >
                                      <CheckIcon
                                        sx={{
                                          color: theme.palette.primary.main,
                                          fontSize: 20,
                                          mt: 0.2,
                                        }}
                                      />
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            color: theme.palette.primary.main,
                                            mb: 0.5,
                                          }}
                                        >
                                          {t("Linked to Story")}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 15,
                                              height: 15,
                                              borderRadius: "3px",
                                              backgroundColor: "#63BA3C",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              flexShrink: 0,
                                            }}
                                          >
                                            <BookmarkIcon
                                              sx={{
                                                fontSize: 10,
                                                color: "#ffffff",
                                              }}
                                            />
                                          </Box>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color:
                                                theme.palette.text.secondary,
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {
                                              stories.find(
                                                (s) => s.id === selectedStory,
                                              )?.summary
                                            }
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Button
                                        size="small"
                                        onClick={() => setSelectedStory(null)}
                                        sx={{
                                          minWidth: "auto",
                                          textTransform: "none",
                                        }}
                                      >
                                        {t("Remove")}
                                      </Button>
                                    </Box>
                                  )}
                                </>
                              ) : (
                                <Box
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : "rgba(0, 0, 0, 0.02)",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      mb: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {t("Create New Story")}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setStoryTab(null);
                                        setStoryCreationData({
                                          summary: "",
                                          description: "",
                                        });
                                      }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Box>

                                  <TextField
                                    fullWidth
                                    size="small"
                                    label={
                                      <>
                                        {t("Summary")}
                                        <Box
                                          component="span"
                                          sx={{
                                            color: theme.palette.error.main,
                                          }}
                                        >
                                          {" "}
                                          *
                                        </Box>
                                      </>
                                    }
                                    placeholder={t("Enter story title...")}
                                    value={storyCreationData.summary}
                                    onChange={(e) =>
                                      setStoryCreationData((prev) => ({
                                        ...prev,
                                        summary: e.target.value,
                                      }))
                                    }
                                    sx={{
                                      mb: 2,
                                      backgroundColor:
                                        theme.palette.background.paper,
                                    }}
                                  />

                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        mb: 1,
                                        display: "block",
                                        fontWeight: 500,
                                        color: theme.palette.text.secondary,
                                      }}
                                    >
                                      {t("Description (Optional)")}
                                    </Typography>
                                    <RichTextEditor
                                      value={storyCreationData.description}
                                      onChange={(value) =>
                                        setStoryCreationData((prev) => ({
                                          ...prev,
                                          description: value,
                                        }))
                                      }
                                      placeholder={t("Add story details...")}
                                    />
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setStoryTab(null);
                                        setStoryCreationData({
                                          summary: "",
                                          description: "",
                                        });
                                      }}
                                      disabled={isCreatingStory}
                                    >
                                      {t("Cancel")}
                                    </Button>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={
                                        isCreatingStory ? (
                                          <CircularProgress size={14} />
                                        ) : (
                                          <AddIcon />
                                        )
                                      }
                                      onClick={handleCreateStory}
                                      disabled={
                                        isCreatingStory ||
                                        !storyCreationData.summary.trim()
                                      }
                                    >
                                      {isCreatingStory
                                        ? t("Creating...")
                                        : t("Create Story")}
                                    </Button>
                                  </Box>
                                </Box>
                              )}

                              {/* Error message for story requirement */}
                              {storyError && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{ mt: 0.5, display: "block" }}
                                >
                                  {storyError}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {shouldShowSprintDropdown &&
                            taskData?.type !== "EPIC" && (
                              <FormControl
                                fullWidth
                                sx={{ mb: 2 }}
                                error={!!sprintError}
                              >
                                <InputLabel
                                  sx={{
                                    transform: "translate(14px, 9px) scale(1)",
                                    "&.Mui-focused, &.MuiFormLabel-filled": {
                                      transform:
                                        "translate(14px, -9px) scale(0.75)",
                                    },
                                    fontWeight: "bold",
                                  }}
                                >
                                  {t("Sprint")}
                                  {/* <Box
                          component="span"
                          sx={{ color: theme.palette.error.main }}
                        >
                          *
                        </Box> */}
                                </InputLabel>
                                <Select
                                  size="small"
                                  value={taskData.currentSprint || ""}
                                  onChange={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const selectedValue = e.target
                                      .value as string;
                                    setTaskData((prev) => {
                                      return {
                                        ...prev,
                                        currentSprint: selectedValue || null,
                                      };
                                    });
                                    // Clear sprint error when a value is selected
                                    if (selectedValue) {
                                      setSprintError("");
                                    }
                                  }}
                                  name="currentSprint"
                                  disabled={
                                    !taskData.project || isSprintLoading
                                  }
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: 300,
                                      },
                                    },
                                  }}
                                  label={t("Sprint")}
                                  sx={{
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: theme.palette.divider,
                                    },
                                  }}
                                  error={!!sprintError}
                                >
                                  <MenuItem value="">
                                    {isSprintLoading
                                      ? t("Loading sprints...")
                                      : t("Select Sprint")}
                                  </MenuItem>

                                  {/* Active Sprints */}
                                  {activeSprints.length > 0 && [
                                    <MenuItem
                                      key="active-header"
                                      disabled
                                      sx={{
                                        opacity: 1,
                                        fontWeight: "bold",
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                        "&.Mui-disabled": {
                                          opacity: 1,
                                        },
                                        pointerEvents: "none", // Prevent any interaction
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: "bold",
                                          color: "text.primary",
                                          fontSize: "0.75rem",
                                        }}
                                      >
                                        {t("Active Sprints")}
                                      </Typography>
                                    </MenuItem>,
                                    ...activeSprints.map((sprint, index) => (
                                      <MenuItem
                                        key={`active-sprint-${sprint._id}-${index}`}
                                        value={sprint._id}
                                        sx={{
                                          pl: 3,
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(0, 0, 0, 0.04)",
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            width: "100%",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 8,
                                              height: 8,
                                              backgroundColor: "#63BA3C",
                                              borderRadius: "50%",
                                            }}
                                          />
                                          <Typography
                                            sx={{ fontSize: "0.875rem" }}
                                          >
                                            {sprint.name}
                                          </Typography>
                                        </Box>
                                      </MenuItem>
                                    )),
                                  ]}

                                  {/* Draft Sprints */}
                                  {draftSprints.length > 0 && [
                                    <MenuItem
                                      key="draft-header"
                                      disabled
                                      sx={{
                                        opacity: 1,
                                        fontWeight: "bold",
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                        "&.Mui-disabled": {
                                          opacity: 1,
                                        },
                                        pointerEvents: "none", // Prevent any interaction
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: "bold",
                                          color: "text.primary",
                                          fontSize: "0.75rem",
                                        }}
                                      >
                                        {t("Draft Sprints")}
                                      </Typography>
                                    </MenuItem>,
                                    ...draftSprints.map((sprint, index) => (
                                      <MenuItem
                                        key={`draft-sprint-${sprint._id}-${index}`}
                                        value={sprint._id}
                                        sx={{
                                          pl: 3,
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(0, 0, 0, 0.04)",
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            width: "100%",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 8,
                                              height: 8,
                                              backgroundColor: "#ff9800",
                                              borderRadius: "50%",
                                            }}
                                          />
                                          <Typography
                                            sx={{ fontSize: "0.875rem" }}
                                          >
                                            {sprint.name}
                                          </Typography>
                                        </Box>
                                      </MenuItem>
                                    )),
                                  ]}
                                </Select>
                                {sprintError && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ mt: 0.5 }}
                                  >
                                    {sprintError}
                                  </Typography>
                                )}
                              </FormControl>
                            )}
                          {/* Epic Selection – show only if issue type is NOT 'EPIC' */}
                          {taskData?.type !== "EPIC" &&
                            taskData?.boardType === "SCRUM" && (
                              <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel
                                  shrink
                                  sx={{
                                    transform: "translate(14px, 9px) scale(1)",
                                    "&.Mui-focused, &.MuiFormLabel-filled": {
                                      transform:
                                        "translate(14px, -9px) scale(0.75)",
                                    },
                                    fontWeight: "bold",
                                  }}
                                >
                                  Epic
                                </InputLabel>
                                <Select
                                  value={selectedEpic || ""}
                                  onChange={(e) =>
                                    setSelectedEpic(e.target.value as string)
                                  }
                                  name="epic"
                                  size="small"
                                  label={t("Epic")}
                                  sx={{
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: theme.palette.divider,
                                    },
                                  }}
                                  // displayEmpty
                                >
                                  <MenuItem value="">
                                    <em>{t("None")}</em>
                                  </MenuItem>
                                  {epics?.map((epic) => (
                                    <MenuItem key={epic.id} value={epic.id}>
                                      {epic.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}

                          <FormControl fullWidth sx={{ mb: 1 }}>
                            <Autocomplete
                              multiple
                              freeSolo
                              limitTags={4}
                              options={[
                                ...availableTags,
                                tagInput &&
                                !availableTags?.some(
                                  (tag) => tag.tagName === tagInput,
                                )
                                  ? { tagName: tagInput }
                                  : null,
                              ]?.filter(Boolean)}
                              getOptionLabel={(option) => option.tagName}
                              disabled={!taskData?.project}
                              disableCloseOnSelect
                              value={availableTags.filter((tag) =>
                                taskData.tags.includes(tag.tagName),
                              )}
                              size="small"
                              onChange={async (_, newValues) => {
                                if (
                                  tagInput &&
                                  !newValues.some(
                                    (tag) => tag.tagName === tagInput,
                                  )
                                ) {
                                  return;
                                }

                                const newTag = newValues.find(
                                  (tag) =>
                                    !availableTags?.some(
                                      (t) => t.tagName === tag.tagName,
                                    ),
                                );

                                if (newTag) {
                                  const payload = {
                                    project: taskData.project,
                                    tagName: newTag.tagName,
                                  };
                                  setIsCreatingTag(true);
                                  try {
                                    const res =
                                      await createTag(payload).unwrap();
                                    if (res) {
                                      await refetchTags();
                                    }
                                  } catch (error) {
                                    console.error("Error creating tag:", error);
                                    setIsCreatingTag(false);
                                  }
                                }

                                setTaskData((prev) => ({
                                  ...prev,
                                  tags: newValues.map((tag) => tag.tagName),
                                }));
                                setIsCreatingTag(false);
                              }}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                }
                              }}
                              onInputChange={(_event, newInputValue) => {
                                setTagInput(newInputValue);
                              }}
                              renderTags={(tagValues, getTagProps) =>
                                tagValues?.map((option, index) => (
                                  <StyledChip
                                    {...getTagProps({ index })}
                                    key={option._id || option.tagName}
                                    label={option.tagName}
                                    icon={
                                      <LocalOfferOutlinedIcon
                                        sx={{
                                          color: theme.palette.primary.main,
                                          fontSize: "14px",
                                        }}
                                      />
                                    }
                                    sx={{
                                      height: "22px",
                                      m: "2px",
                                      maxWidth: "calc(100% - 2px)",
                                      "& .MuiChip-label": {
                                        paddingLeft: "10px",
                                      },
                                    }}
                                  />
                                ))
                              }
                              renderOption={(props, option, { selected }) => {
                                const isCustomTag =
                                  option.tagName === tagInput &&
                                  tagInput !== "";
                                return (
                                  <li {...props}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "100%",
                                      }}
                                    >
                                      {!isCustomTag ? (
                                        <LocalOfferOutlinedIcon
                                          sx={{
                                            marginRight: 1,
                                            fontSize: "15px",
                                          }}
                                        />
                                      ) : null}
                                      <Typography
                                        sx={{
                                          flexGrow: 1,
                                          fontSize: "13px",
                                          display: "inline-flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        {isCustomTag ? (
                                          <>
                                            <AddCircleOutlineOutlinedIcon
                                              sx={{
                                                fontSize: "18px",
                                                mr: 1,
                                                color: "#63BA3C",
                                              }}
                                            />
                                            {tagInput}{" "}
                                          </>
                                        ) : (
                                          option.tagName
                                        )}
                                      </Typography>
                                      {selected && (
                                        <CheckIcon
                                          sx={{
                                            color: theme.palette.primary.main,
                                            ml: 1,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </li>
                                );
                              }}
                              renderInput={(params) => (
                                <StyledTextField
                                  {...params}
                                  label={t("Tags")}
                                  placeholder={t("Add Tags")}
                                  variant="outlined"
                                  fullWidth
                                  slotProps={{
                                    input: {
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {isCreatingTag ? (
                                            <CircularProgress
                                              color="inherit"
                                              size={20}
                                            />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    },
                                  }}
                                  sx={{
                                    marginBottom: "0px",
                                    "& .MuiInputBase-root": {
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                      alignItems: "center",
                                      p: 1,
                                    },
                                    "& .MuiInputBase-input": {
                                      position: "relative",
                                      width: "auto",
                                      minWidth: "60px",
                                      flexGrow: 1,
                                    },
                                  }}
                                />
                              )}
                              sx={{
                                mb: 2,
                                "& .MuiAutocomplete-tag": {
                                  margin: "2px",
                                },
                                "& .MuiInput-root": {
                                  display: "flex",
                                  flexWrap: "wrap",
                                  border: `1px solid ${theme.palette.divider}`,
                                  gap: "4px",
                                  alignItems: "flex-start",
                                  padding: "8px",
                                  maxHeight: "120px",
                                  overflow: "auto",
                                },
                              }}
                            />
                          </FormControl>

                          {/* Estimation - Days, Hours and Minutes */}
                          {isAta && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 1,
                                  fontSize: "13px",
                                  color: theme.palette.text.primary,
                                }}
                              >
                                {t("Estimation")}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <TextField
                                  size="small"
                                  label={t("Days")}
                                  type="number"
                                  value={taskData.estimationDays || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (
                                      value === "" ||
                                      (parseInt(value) >= 0 &&
                                        !isNaN(parseInt(value)))
                                    ) {
                                      handleEstimationDaysChange(value);
                                    }
                                  }}
                                  inputProps={{
                                    min: 0,
                                    step: 1,
                                    onKeyDown: (e) => {
                                      if (
                                        ["-", "e", "E", "+", "."].includes(
                                          e.key,
                                        )
                                      ) {
                                        e.preventDefault();
                                      }
                                    },
                                  }}
                                  sx={{ flex: "1 1 60px", minWidth: 60 }}
                                />
                                <TextField
                                  size="small"
                                  label={t("Hours")}
                                  type="number"
                                  value={taskData.estimationHours || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (
                                      value === "" ||
                                      (parseInt(value) >= 0 &&
                                        parseInt(value) <= 23 &&
                                        !isNaN(parseInt(value)))
                                    ) {
                                      handleEstimationHoursChange(value);
                                    }
                                  }}
                                  inputProps={{
                                    min: 0,
                                    max: 23,
                                    step: 1,
                                    onKeyDown: (e) => {
                                      if (
                                        ["-", "e", "E", "+", "."].includes(
                                          e.key,
                                        )
                                      ) {
                                        e.preventDefault();
                                      }
                                    },
                                  }}
                                  sx={{ flex: "1 1 60px", minWidth: 60 }}
                                />
                                <FormControl
                                  size="small"
                                  sx={{ flex: "1 1 80px", minWidth: 80 }}
                                >
                                  <InputLabel
                                    sx={{
                                      transform:
                                        "translate(14px, 9px) scale(1)",
                                      "&.Mui-focused, &.MuiFormLabel-filled": {
                                        transform:
                                          "translate(14px, -9px) scale(0.75)",
                                      },
                                    }}
                                  >
                                    {t("Minutes")}
                                  </InputLabel>
                                  <Select
                                    value={taskData.estimationMinutes || ""}
                                    onChange={(e) =>
                                      handleEstimationMinutesChange(
                                        e.target.value as string,
                                      )
                                    }
                                    label={t("Minutes")}
                                  >
                                    {minuteOptions.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                              {taskData.estimation && (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  sx={{ mt: 0.5, display: "block" }}
                                >
                                  {t("Estimation")}: {taskData.estimation}
                                </Typography>
                              )}
                            </Box>
                          )}
                          {taskData?.type !== "EPIC" &&
                            taskData?.boardType === "SCRUM" &&
                            isAta && (
                              <>
                                <StyledTextField
                                  label={t("Start Date")}
                                  type="date"
                                  name="startDate"
                                  value={taskData.startDate || ""}
                                  onChange={(e) =>
                                    setTaskData((prev) => ({
                                      ...prev,
                                      startDate: e.target.value,
                                    }))
                                  }
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  variant="outlined"
                                  fullWidth
                                  sx={{ mb: 2 }}
                                  size="small"
                                />
                                <StyledTextField
                                  label={t("Due Date")}
                                  type="date"
                                  name="dueDate"
                                  value={taskData.dueDate || ""}
                                  onChange={(e) =>
                                    setTaskData((prev) => ({
                                      ...prev,
                                      dueDate: e.target.value,
                                    }))
                                  }
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  variant="outlined"
                                  fullWidth
                                  sx={{ mb: 2 }}
                                  size="small"
                                />
                              </>
                            )}

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={taskData?.keepSettings}
                                onChange={(e) =>
                                  setTaskData((prev) => ({
                                    ...prev,
                                    keepSettings: e.target.checked,
                                  }))
                                }
                                name="keepSettings"
                                disabled={!taskData.project} // Only disable when no project is selected
                              />
                            }
                            label={t("Keep these settings")}
                          />
                        </Box>
                      </>
                    )}
                    {activeTab === "2" && (
                      <Box>
                        {jiraProjectList?.length > 0 &&
                          taskData.project &&
                          defaultFields &&
                          Object.entries(defaultFields)?.map(
                            ([fieldLabel, fieldData]: any) => {
                              const fieldKeys = Object.keys(fieldData).filter(
                                (key) => key !== "method" && key !== "type",
                              );
                              const fieldKey = fieldKeys[0];
                              const defaultValue = fieldData[fieldKey];
                              const type = fieldData.type;

                              if (fieldData?.allowedValues) {
                                return (
                                  <StyledSelect
                                    key={fieldLabel}
                                    value={formData[fieldKey] || ""}
                                    onChange={(e) => {
                                      // @ts-ignore
                                      handleChange(
                                        fieldKey,
                                        // @ts-ignore
                                        e.target.value,
                                        type,
                                      );
                                    }}
                                    name={fieldLabel}
                                    displayEmpty
                                    fullWidth
                                    sx={{ mb: 2 }}
                                  >
                                    <MenuItem selected disabled value="">
                                      {fieldLabel}
                                    </MenuItem>
                                    {fieldData?.allowedValues?.map((option) => (
                                      <MenuItem
                                        key={option?.id}
                                        value={option?.id}
                                      >
                                        {option?.name}
                                      </MenuItem>
                                    ))}
                                  </StyledSelect>
                                );
                              }

                              return (
                                <Box key={fieldLabel} sx={{ mb: 2 }}>
                                  <TextField
                                    fullWidth
                                    type={type}
                                    label={fieldLabel}
                                    value={formData[fieldKey] || defaultValue}
                                    onChange={(e) => {
                                      handleChange(
                                        fieldKey,
                                        e.target.value,
                                        type,
                                      );
                                    }}
                                    inputProps={
                                      type === "number"
                                        ? {
                                            min: 0,
                                            onKeyDown: (e) => {
                                              if (
                                                ["-", "e", "E"].includes(e.key)
                                              ) {
                                                e.preventDefault();
                                              }
                                            },
                                          }
                                        : undefined
                                    }
                                    size="small"
                                  />
                                </Box>
                              );
                            },
                          )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Allotment.Pane>
            )}
          </Allotment>
        </Box>
      </StyledDialog>

      <AnnotationEditor
        open={isAnnotationEditorOpen}
        onClose={() => setIsAnnotationEditorOpen(false)}
        imageUrl={taskData?.screenshot || taskData?.fullPageScreenshot || ""}
        onSave={handleSaveAnnotation}
        onInsertText={(text) =>
          setTaskData((prev) => ({
            ...prev,
            description: (prev.description || "") + "\n" + text,
          }))
        }
      />

      <AnnotationEditor
        open={isAnnotatingAttachment}
        onClose={() => {
          setIsAnnotatingAttachment(false);
          setCurrentAttachmentIndex(null);
        }}
        imageUrl={
          currentAttachmentIndex !== null && attachments[currentAttachmentIndex]
            ? attachments[currentAttachmentIndex].url
            : ""
        }
        onSave={handleSaveAttachmentAnnotation}
        onInsertText={(text) =>
          setTaskData((prev) => ({
            ...prev,
            description: (prev.description || "") + "\n" + text,
          }))
        }
      />

      <AttachmentViewModal
        open={isAttachmentViewOpen}
        onClose={() => setIsAttachmentViewOpen(false)}
        attachment={selectedAttachment}
      />

      <Dialog
        open={isImagePreviewOpen}
        onClose={() => setIsImagePreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <IconButton
          onClick={() => setIsImagePreviewOpen(false)}
          sx={{
            color: (theme) => theme.palette.grey[500],
            position: "absolute",
            right: 16,
            top: 16,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{ padding: 2, display: "flex", justifyContent: "center", mt: 5 }}
        >
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Preview"
              style={{ width: "100%", maxHeight: "85vh", objectFit: "contain" }}
            />
          )}
        </Box>
      </Dialog>

      {/* Video Preview Panel Modal */}
      <Dialog
        open={isVideoPreviewOpen}
        onClose={() => setIsVideoPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <VideoPreviewPanel
          videoUrl={previewVideoUrl || ""}
          networkLogs={networkLogs || []}
          consoleLogs={consoleLogs || []}
          onClose={() => setIsVideoPreviewOpen(false)}
          isOpen={isVideoPreviewOpen}
        />
      </Dialog>

      <AiNotConfiguredDialog
        open={showAiConfigDialog}
        onClose={() => setShowAiConfigDialog(false)}
      />
    </>
  );
};

export default TaskCreateModal;
